import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, BookOpen, Building2, Camera, Check, Edit3, FileText, HandCoins, Keyboard, MapPinned, PackagePlus, Percent, Printer, QrCode, RefreshCw, Save, Search, SearchCheck, Settings, SlidersHorizontal, Smartphone, SunMoon, Tags, Trash2, X } from "lucide-react";
import type { BookMetadata, CatalogFormat, InventoryDefaults, InventoryLocation, InventoryShelf, LabelBatch, LabelInventoryItem, LabelTemplate, Money, OperationalCondition, PosDiscountDefinition, ProductType, PublicStoreProfile, ReceivingBatch, ReceivingLine, ReceivingResolution, UserSession } from "../shared/types";
import type { ModuleKey, OrganizationModuleView } from "../shared/modules";
import { getModuleDefinition } from "../shared/modules";
import { isValidIsbn, normalizeIsbn } from "../shared/isbn";
import { StaffApiError, staffErrorMessage } from "../client/api";
import { productBrand } from "../brand";
import { defaultSupplierSearchProviders } from "../shared/schemas";

type ApiClient = <T>(path: string, init?: RequestInit) => Promise<T>;
type ImportPriceKey = "hardcover" | "softcover" | "mass_market_paperback" | "new" | "dvd" | "blu_ray" | "vhs" | "cd" | "vinyl" | "cassette" | "video_game" | "merchandise" | "other";
type LabelWizardStep = "find" | "queue" | "preview";
type ReceivingAppSettings = { defaultCurrency: string; defaultImportPrices: Record<ImportPriceKey, Money> };
type SupplierSearchProvider = {
  id: string;
  name: string;
  enabled: boolean;
  mode: "link" | "ebay_api" | "google_books" | "serpapi_google_shopping" | "wholesale_fixture" | "wholesale_recorded_fixture" | "pubeasy_soap" | "ingram_ipage" | "direct_supplier_api";
  providerType?: "local_inventory" | "incoming_inventory" | "wholesale" | "marketplace" | "metadata";
  capabilities?: string[];
  credentialRequirements?: string[];
  urlTemplate: string;
};
type SupplierSearchDiagnostics = {
  serpApiConfigured: boolean;
  bindings: { SERPAPI_API_KEY?: boolean; SERAPI_API_KEY?: boolean };
  providers: { id: string; name: string; enabled: boolean; mode: string }[];
};
type DetectedBarcode = { rawValue: string };
type BarcodeDetectorLike = {
  detect(source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement | ImageBitmap): Promise<DetectedBarcode[]>;
};
type BarcodeDetectorConstructor = new (options: { formats: string[] }) => BarcodeDetectorLike;
type ScannerSession = { id: string; targetId: string; url: string; status: string; expiresAt: string };
type ScannerScan = { id: string; input: string; status: string; message?: string; createdAt: string };
type ReceivingRestoreState = { batchId?: string; scanner?: ScannerSession; savedAt: string };

const receivingRestoreKey = "paperbackos:receiving:active";

const defaultStoreProfile: PublicStoreProfile = {
  productName: productBrand.name,
  storeDisplayName: "Independent Bookstore",
  storeTagline: "Independent bookstore",
  storeDescription: "Browse available items for pickup.",
  logoUrl: null,
  publicWebsiteUrl: null,
  publicEmail: null,
  publicPhone: null,
  addressLine1: null,
  addressLine2: null,
  city: null,
  region: null,
  postalCode: null,
  country: null,
  socialLinks: {},
  businessHours: [],
  timezone: null,
  currencyCode: "USD",
  receiptFooterText: null,
  storefrontTitle: "Independent Bookstore",
  storefrontDescription: "Browse available items for pickup.",
};

type DraftLine = {
  lineId?: string;
  sourceIdentifier?: string;
  editionId?: string;
  variationId?: string;
  workTitle: string;
  contributorDisplay: string;
  isbn: string;
  productType: ProductType;
  formatCode: string;
  trackingMode: "copy" | "quantity";
  quantity: number;
  unitCostAmount: number;
  unitSellingPriceAmount: number;
  conditionCode: string;
  conditionNotes: string;
  shelfCode: string;
  storeBarcodesText: string;
  metadata?: BookMetadata;
};

type InventorySettingsResponse = {
  locations?: InventoryLocation[];
  shelves?: InventoryShelf[];
  formats?: CatalogFormat[];
  conditions?: OperationalCondition[];
  defaults?: InventoryDefaults;
};
type TradeInCreditMode = "dollar_amount" | "one_for_one";
type SettingsSectionId = "appearance" | "operations" | "discounts" | "tradeIns" | "profile" | "suppliers" | "structure" | "catalog" | "labels" | "modules";
type SettingsAreaCard = {
  title: string;
  eyebrow: string;
  summary: string;
  path: string;
  icon: typeof Settings;
  roles: UserSession["role"][];
};
type ThemeMode = "light" | "dark";
type TradeInRedemptionRule = { enabled: boolean; maxPercent: number };
type TradeInRedemptionRules = { default: TradeInRedemptionRule; categories: Record<string, TradeInRedemptionRule> };
type InventoryRequestMatch = {
  requestId: string;
  customerId?: string;
  customerDisplayName?: string;
  title: string;
  author?: string;
  isbn?: string;
  status: string;
  matchedBy: "isbn" | "title_author" | "title";
  inventoryTitle: string;
  variationId?: string;
};
type RequestMatchUpdate = { requestId: string; status: string };
type TradeInPolicySettings = {
  enabled: boolean;
  version?: number;
  creditMode: TradeInCreditMode;
  defaultCreditPercentage: number;
  oneForOneCreditAmount: number;
  minItemPriceAmount: number;
  maxItemPriceAmount: number;
  currency: string;
  quoteExpirationMinutes: number;
  requireCustomer: boolean;
  requireInspection: boolean;
  managerApprovalThresholdAmount: number;
  conditionAdjustments: Record<string, number>;
  allowedReasons: string[];
  redemptionRules: TradeInRedemptionRules;
  modeChangeConversionAmount?: number;
  policyText: string | null;
};
type LabelStyleSettings = { labelBackgroundColor: string; labelTextColor: string };
type DiscountDraft = {
  id?: string;
  name: string;
  discountType: PosDiscountDefinition["discountType"];
  value: string;
  scope: PosDiscountDefinition["scope"];
  applicationMode: PosDiscountDefinition["applicationMode"];
  active: boolean;
  minimumSubtotal: string;
  minimumItemCount: string;
  minimumMatchingItemCount: string;
  productType: "" | ProductType;
  formatMatch: string;
  conditionMatch: string;
  titleMatch: string;
  startsAt: string;
  endsAt: string;
  startTime: string;
  endTime: string;
  daysOfWeek: string[];
  priority: number;
  stackable: boolean;
};

const daysOfWeekOptions = [
  { value: "sunday", label: "Sun" },
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
];

const defaultLine: DraftLine = {
  workTitle: "",
  contributorDisplay: "",
  isbn: "",
  productType: "book",
  formatCode: "other",
  trackingMode: "quantity",
  quantity: 1,
  unitCostAmount: 0,
  unitSellingPriceAmount: 0,
  conditionCode: "good",
  conditionNotes: "",
  shelfCode: "",
  storeBarcodesText: "",
};

const productTypeOptions: { value: ProductType; label: string }[] = [
  { value: "book", label: "Book" },
  { value: "movie", label: "Movie" },
  { value: "music", label: "Music" },
  { value: "game", label: "Game" },
  { value: "merchandise", label: "Merchandise" },
  { value: "other", label: "Other" },
];

const defaultReceivingAppSettings: ReceivingAppSettings = {
  defaultCurrency: "USD",
  defaultImportPrices: {
    hardcover: { amount: 1300, currency: "USD" },
    softcover: { amount: 700, currency: "USD" },
    mass_market_paperback: { amount: 500, currency: "USD" },
    new: { amount: 2000, currency: "USD" },
    dvd: { amount: 500, currency: "USD" },
    blu_ray: { amount: 700, currency: "USD" },
    vhs: { amount: 300, currency: "USD" },
    cd: { amount: 500, currency: "USD" },
    vinyl: { amount: 1500, currency: "USD" },
    cassette: { amount: 400, currency: "USD" },
    video_game: { amount: 1500, currency: "USD" },
    merchandise: { amount: 500, currency: "USD" },
    other: { amount: 500, currency: "USD" },
  },
};

const receivingPriceDefaultOptions: { key: ImportPriceKey; label: string }[] = [
  { key: "softcover", label: "Paperback" },
  { key: "hardcover", label: "Hardcover" },
  { key: "mass_market_paperback", label: "Mass market" },
  { key: "new", label: "New item" },
  { key: "dvd", label: "DVD" },
  { key: "blu_ray", label: "Blu-ray" },
  { key: "vhs", label: "VHS" },
  { key: "cd", label: "CD" },
  { key: "vinyl", label: "Vinyl" },
  { key: "cassette", label: "Cassette" },
  { key: "video_game", label: "Video game" },
  { key: "merchandise", label: "Merchandise" },
  { key: "other", label: "Other" },
];

const defaultTradeInPolicySettings: TradeInPolicySettings = {
  enabled: true,
  creditMode: "dollar_amount",
  defaultCreditPercentage: 50,
  oneForOneCreditAmount: 1,
  minItemPriceAmount: 0,
  maxItemPriceAmount: 1_000_000,
  currency: "USD",
  quoteExpirationMinutes: 1440,
  requireCustomer: false,
  requireInspection: true,
  managerApprovalThresholdAmount: 5000,
  conditionAdjustments: { new: 100, like_new: 90, very_good: 80, good: 70, acceptable: 50, poor: 0 },
  allowedReasons: ["standard_trade_in", "condition_reject", "policy_reject", "customer_cancelled", "other"],
  redemptionRules: {
    default: { enabled: true, maxPercent: 100 },
    categories: {
      used: { enabled: true, maxPercent: 100 },
      new: { enabled: true, maxPercent: 100 },
      hardcover: { enabled: true, maxPercent: 100 },
      softcover: { enabled: true, maxPercent: 100 },
    },
  },
  policyText: null,
};

const tradeInRedemptionCategories = [
  { key: "default", label: "Default" },
  { key: "used", label: "Used" },
  { key: "new", label: "New" },
  { key: "hardcover", label: "Hardcover" },
  { key: "softcover", label: "Softcover" },
];
const defaultLabelStyleSettings: LabelStyleSettings = { labelBackgroundColor: "#FFFFFF", labelTextColor: "#111111" };
const emptyDiscountDraft: DiscountDraft = {
  name: "",
  discountType: "percentage",
  value: "",
  scope: "order",
  applicationMode: "automatic",
  active: true,
  minimumSubtotal: "",
  minimumItemCount: "",
  minimumMatchingItemCount: "",
  productType: "",
  formatMatch: "",
  conditionMatch: "",
  titleMatch: "",
  startsAt: "",
  endsAt: "",
  startTime: "",
  endTime: "",
  daysOfWeek: [],
  priority: 100,
  stackable: true,
};

export function ReceivingOperationsPage({ api, storeProfile, currentUser }: { api: ApiClient; storeProfile: PublicStoreProfile; currentUser: UserSession }) {
  const scanRef = useRef<HTMLInputElement | null>(null);
  const photoScanRef = useRef<HTMLInputElement | null>(null);
  const activeBatchRef = useRef<ReceivingBatch | null>(null);
  const savedLinesRef = useRef<Record<string, ReceivingLine[]>>({});
  const postSaveRefreshTimers = useRef<Map<string, number>>(new Map());
  const scannerProcessingRef = useRef(false);
  const resolveScanIntoBatchRef = useRef<((detectedValue?: string, options?: { targetBatch?: ReceivingBatch; suppressMessage?: boolean; allowManualReview?: boolean; deferRefresh?: boolean }) => Promise<{ saved: boolean; duplicate: boolean; manualReview: boolean; title?: string }>) | null>(null);
  const [batches, setBatches] = useState<ReceivingBatch[]>([]);
  const [batch, setBatch] = useState<ReceivingBatch | null>(null);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [shelves, setShelves] = useState<InventoryShelf[]>([]);
  const [conditions, setConditions] = useState<OperationalCondition[]>([]);
  const [defaults, setDefaults] = useState<InventoryDefaults | null>(null);
  const [appSettings, setAppSettings] = useState<ReceivingAppSettings>(defaultReceivingAppSettings);
  const [rawAppSettings, setRawAppSettings] = useState<Record<string, unknown>>({});
  const [targetLocationId, setTargetLocationId] = useState("");
  const [scanValue, setScanValue] = useState("");
  const [line, setLine] = useState<DraftLine>(defaultLine);
  const [savedLines, setSavedLines] = useState<Record<string, ReceivingLine[]>>({});
  const [visibleLineRevision, setVisibleLineRevision] = useState(0);
  const [lineReviewBatch, setLineReviewBatch] = useState<ReceivingBatch | null>(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [completionPending, setCompletionPending] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [phoneScanner, setPhoneScanner] = useState<ScannerSession | null>(null);
  const [phoneScannerMessage, setPhoneScannerMessage] = useState("");
  const [photoScanSummary, setPhotoScanSummary] = useState("");
  const [lastCompletion, setLastCompletion] = useState<{ createdMovements: number; createdCopies: number; labelBatchId?: string; labelError?: string; batch: ReceivingBatch; requestMatches?: InventoryRequestMatch[] } | null>(null);
  const [requestMatches, setRequestMatches] = useState<InventoryRequestMatch[]>([]);

  const canMutateReceiving = currentUser.role === "administrator" || currentUser.role === "manager";
  const activeLocation = locations.find((location) => location.id === targetLocationId);
  const reviewBaseBatch = lineReviewBatch ?? batch;
  const displayBatch = useMemo(() => reviewBaseBatch ? mergeReceivingLines(reviewBaseBatch, savedLinesRef.current[reviewBaseBatch.id] ?? savedLines[reviewBaseBatch.id]) : null, [reviewBaseBatch, savedLines, visibleLineRevision]);
  const blockingLines = (displayBatch?.lines ?? []).filter((entry) => receivingLineBlockingIssues(entry).length > 0);
  const editableBatch = batch && ["draft", "in_progress", "partially_completed", "correction_required"].includes(batch.status);
  const canSaveDialogLine = canMutateReceiving && Boolean(line.workTitle.trim()) && !pending && (Boolean(editableBatch) || (!line.lineId && Boolean(targetLocationId)));
  const quantitySummary = useMemo(() => summarizeBatch(displayBatch), [displayBatch]);
  const phoneScannerHostAvailable = useMemo(() => !isPhoneLikeDevice(), []);
  const photoScanAvailable = useMemo(() => isPhoneLikeDevice(), []);

  const setActiveBatch = useCallback((next: ReceivingBatch | null | ((current: ReceivingBatch | null) => ReceivingBatch | null)) => {
    const resolved = typeof next === "function" ? next(activeBatchRef.current) : next;
    activeBatchRef.current = resolved;
    setBatch(resolved);
  }, []);

  const rememberReceivingRestoreState = useCallback((nextBatch: ReceivingBatch | null, scanner = phoneScanner) => {
    if (!nextBatch && !scanner) {
      localStorage.removeItem(receivingRestoreKey);
      return;
    }
    localStorage.setItem(receivingRestoreKey, JSON.stringify({
      batchId: nextBatch?.id ?? scanner?.targetId,
      scanner: scanner ?? undefined,
      savedAt: new Date().toISOString(),
    } satisfies ReceivingRestoreState));
  }, [phoneScanner]);

  const clearReceivingRestoreState = useCallback(() => {
    localStorage.removeItem(receivingRestoreKey);
  }, []);

  const setActiveBatchPreservingSavedLines = useCallback((next: ReceivingBatch | null, preserveLine?: ReceivingLine) => {
    setActiveBatch((current) => {
      if (!next) return next;
      const currentLines = current?.id === next.id ? current.lines : undefined;
      return mergeReceivingLines(next, preserveLine ? upsertLineList(currentLines ?? [], preserveLine) : currentLines);
    });
  }, [setActiveBatch]);

  const rememberBatchLines = useCallback((batchId: string, lines?: ReceivingLine[], preserveLine?: ReceivingLine) => {
    setSavedLines((current) => {
      const existing = current[batchId] ?? [];
      const merged = [...(lines?.length ? lines : existing)];
      const nextLines = preserveLine ? upsertLineList(merged, preserveLine) : merged;
      const normalized = nextLines.map(normalizeReceivingLineForDisplay);
      savedLinesRef.current = { ...savedLinesRef.current, [batchId]: normalized };
      return { ...current, [batchId]: normalized };
    });
    setVisibleLineRevision((revision) => revision + 1);
  }, []);

  const rememberSavedLineImmediately = useCallback((batchId: string, savedLine: ReceivingLine) => {
    const normalized = normalizeReceivingLineForDisplay(savedLine);
    const nextLines = upsertLineList(savedLinesRef.current[batchId] ?? [], normalized);
    savedLinesRef.current = { ...savedLinesRef.current, [batchId]: nextLines };
    setSavedLines((current) => ({ ...current, [batchId]: nextLines }));
    setVisibleLineRevision((revision) => revision + 1);
  }, []);

  const forgetBatchLine = useCallback((batchId: string, lineId: string) => {
    setSavedLines((current) => {
      const nextLines = (current[batchId] ?? []).filter((entry) => entry.id !== lineId);
      savedLinesRef.current = { ...savedLinesRef.current, [batchId]: nextLines };
      return { ...current, [batchId]: nextLines };
    });
  }, []);

  const load = useCallback(async () => {
    setError(null);
    const [settingsData, batchData, appSettingsData] = await Promise.all([
      api<InventorySettingsResponse>("/api/admin/inventory-settings"),
      api<{ batches: ReceivingBatch[] }>("/api/admin/receiving/batches"),
      api<{ settings: Record<string, unknown> }>("/api/admin/settings").catch((): { settings: Record<string, unknown> } => ({ settings: {} })),
    ]);
    const activeLocations = (settingsData.locations ?? []).filter((location) => location.active);
    setLocations(activeLocations);
    setShelves(settingsData.shelves ?? []);
    setConditions(settingsData.conditions ?? []);
    setDefaults(settingsData.defaults ?? null);
    const batchList = batchData.batches ?? [];
    setBatches(batchList);
    setAppSettings(mergeReceivingAppSettings(appSettingsData.settings, storeProfile.currencyCode ?? settingsData.defaults?.defaultCurrency));
    setRawAppSettings(appSettingsData.settings ?? {});
    const preferredLocation = targetLocationId || settingsData.defaults?.defaultLocationId || activeLocations[0]?.id || "";
    if (preferredLocation && activeLocations.some((location) => location.id === preferredLocation)) setTargetLocationId(preferredLocation);
    const defaultConditionCode = conditionCodeFromDefault(settingsData.conditions ?? [], settingsData.defaults?.defaultConditionId);
    if (defaultConditionCode) {
      setLine((current) => current.lineId || current.workTitle || current.isbn ? current : { ...current, conditionCode: defaultConditionCode, trackingMode: settingsData.defaults?.defaultTrackingMode ?? current.trackingMode });
    }
    if (!activeBatchRef.current) {
      const restore = readReceivingRestoreState();
      const restoreBatchId = restore?.batchId;
      const restoreCandidate = restoreBatchId ? batchList.find((entry) => entry.id === restoreBatchId) : undefined;
      if (restoreCandidate && ["draft", "in_progress", "partially_completed", "correction_required"].includes(restoreCandidate.status)) {
        const restored = await readBatch(api, restoreCandidate.id);
        setActiveBatch(restored);
        setLineReviewBatch(restored);
        rememberBatchLines(restored.id, restored.lines);
        setTargetLocationId(restored.targetLocationId);
        if (restore?.scanner && new Date(restore.scanner.expiresAt).getTime() > Date.now()) {
          setPhoneScanner(restore.scanner);
          setPhoneScannerMessage("Phone scanner restored. Scans will continue landing in this batch.");
        }
        setMessage(`Restored ${restored.receivingNumber}`);
      }
    }
  }, [api, setActiveBatch, storeProfile.currencyCode, targetLocationId]);

  useEffect(() => {
    void load().catch((caught) => setError(caught));
  }, [load]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        scanRef.current?.focus();
      } else if (event.key === "Escape") {
        setSetupOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    return () => {
      postSaveRefreshTimers.current.forEach((timer) => window.clearTimeout(timer));
      postSaveRefreshTimers.current.clear();
    };
  }, []);

  const runMutation = async (label: string, action: () => Promise<void>) => {
    setPending(label);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(null);
    }
  };

  const refreshBatch = async (batchId = batch?.id, preserveLine?: ReceivingLine) => {
    if (!batchId) return;
    const data = await api<{ batch: ReceivingBatch }>(`/api/admin/receiving/batches/${encodeURIComponent(batchId)}`);
    const refreshed = preserveLine ? upsertReceivingLine(data.batch, preserveLine) : data.batch;
    setActiveBatchPreservingSavedLines(refreshed, preserveLine);
    setLineReviewBatch(refreshed);
    rememberBatchLines(refreshed.id, refreshed.lines, preserveLine);
    const batchData = await api<{ batches: ReceivingBatch[] }>("/api/admin/receiving/batches");
    setBatches(batchData.batches ?? []);
  };

  const queuePostSaveRefresh = (batchId: string, preserveLine: ReceivingLine) => {
    const existingTimer = postSaveRefreshTimers.current.get(batchId);
    if (existingTimer) window.clearTimeout(existingTimer);
    const timer = window.setTimeout(() => {
      postSaveRefreshTimers.current.delete(batchId);
      void refreshBatch(batchId, preserveLine).catch((caught) => setError(caught));
    }, 900);
    postSaveRefreshTimers.current.set(batchId, timer);
  };

  const refreshBatchUntilLineVisible = async (targetBatch: ReceivingBatch, line: ReceivingLine) => {
    let latest: ReceivingBatch | null = null;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const data = await api<{ batch: ReceivingBatch }>(`/api/admin/receiving/batches/${encodeURIComponent(targetBatch.id)}`);
      latest = data.batch;
      if (batchContainsLine(data.batch, line)) {
        const refreshed = upsertReceivingLine(data.batch, line);
        setActiveBatchPreservingSavedLines(refreshed, line);
        setLineReviewBatch(refreshed);
        rememberBatchLines(refreshed.id, refreshed.lines, line);
        const batchData = await api<{ batches: ReceivingBatch[] }>("/api/admin/receiving/batches");
        setBatches(batchData.batches ?? []);
        return;
      }
      if (attempt < 3) await delay(125 * (attempt + 1));
    }
    const fallback = upsertReceivingLine(latest ?? targetBatch, line);
    setActiveBatchPreservingSavedLines(fallback, line);
    setLineReviewBatch(fallback);
    rememberBatchLines(fallback.id, fallback.lines, line);
    const batchData = await api<{ batches: ReceivingBatch[] }>("/api/admin/receiving/batches");
    setBatches(batchData.batches ?? []);
  };

  const createBatch = async (): Promise<ReceivingBatch> => {
    if (!targetLocationId) throw new StaffApiError({ status: 409, code: "unauthorized_location", message: "Choose an active receiving location first" });
    const data = await api<{ batch: ReceivingBatch }>("/api/admin/receiving/batches", {
      method: "POST",
      body: JSON.stringify({ targetLocationId, receivingSource: "manual", idempotencyKey: crypto.randomUUID() }),
    });
    setActiveBatch(data.batch);
    setLineReviewBatch(data.batch);
    rememberBatchLines(data.batch.id, data.batch.lines);
    rememberReceivingRestoreState(data.batch);
    setLastCompletion(null);
    return data.batch;
  };

  const startBatch = () => runMutation("start-batch", async () => {
    const data = await createBatch();
    setMessage(`Started ${data.receivingNumber}`);
    await refreshBatch(data.id);
    setSetupOpen(false);
    scanRef.current?.focus();
  });

  const resumeBatch = (batchId: string) => runMutation("resume-batch", async () => {
    const data = await readBatch(api, batchId);
    setActiveBatch(data);
    setLineReviewBatch(data);
    rememberBatchLines(data.id, data.lines);
    setTargetLocationId(data.targetLocationId);
    rememberReceivingRestoreState(data);
    setLastCompletion(null);
    setMessage(`Resumed ${data.receivingNumber}`);
    setSetupOpen(false);
    scanRef.current?.focus();
  });

  const setDefaultImportPrice = (key: ImportPriceKey, value: string) => {
    const amount = inputToMinorUnits(value);
    setAppSettings((current) => ({
      ...current,
      defaultImportPrices: {
        ...current.defaultImportPrices,
        [key]: { amount, currency: current.defaultCurrency },
      },
    }));
  };

  const saveBatchSettings = () => runMutation("save-batch-settings", async () => {
    if (!defaults) throw new StaffApiError({ status: 409, code: "settings_not_loaded", message: "Settings are still loading. Try again in a moment." });
    if (!targetLocationId) throw new StaffApiError({ status: 409, code: "location_required", message: "Choose a receiving location first." });
    const defaultConditionId = defaultConditionIdFromCode(conditions, line.conditionCode, defaults.defaultConditionId);
    const nextDefaults: InventoryDefaults = {
      ...defaults,
      defaultLocationId: targetLocationId,
      defaultConditionId,
    };
    const nextAppSettings = mergeReceivingAppSettings(appSettings, defaults.defaultCurrency ?? storeProfile.currencyCode);
    const nextRawSettings = {
      ...rawAppSettings,
      defaultCurrency: nextAppSettings.defaultCurrency,
      defaultImportPrices: nextAppSettings.defaultImportPrices,
    };
    const [defaultsData, settingsData] = await Promise.all([
      api<{ defaults: InventoryDefaults }>("/api/admin/inventory-settings", {
        method: "PUT",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify(nextDefaults),
      }),
      api<{ settings: Record<string, unknown> }>("/api/admin/settings", {
        method: "PUT",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify(nextRawSettings),
      }),
    ]);
    setDefaults(defaultsData.defaults);
    setRawAppSettings(settingsData.settings);
    setAppSettings(mergeReceivingAppSettings(settingsData.settings, defaultsData.defaults.defaultCurrency ?? storeProfile.currencyCode));
    setLine((current) => ({ ...current, conditionCode: conditionCodeFromDefault(conditions, defaultsData.defaults.defaultConditionId) ?? current.conditionCode }));
    setMessage("Batch settings saved");
  });

  const findDuplicateReceivingLine = (targetBatch: ReceivingBatch, draft: DraftLine): ReceivingLine | undefined => {
    if (defaults?.duplicateScanBehavior !== "increment_quantity" || draft.trackingMode !== "quantity") return undefined;
    const normalizedIsbn = normalizeIsbn(draft.isbn);
    const normalizedSource = normalizeScannedIdentifier(draft.sourceIdentifier ?? draft.metadata?.barcode ?? "");
    const lines = [...(targetBatch.lines ?? []), ...(savedLinesRef.current[targetBatch.id] ?? [])]
      .map(normalizeReceivingLineForDisplay);
    const uniqueLines = Array.from(new Map(lines.map((entry) => [entry.id, entry])).values());
    return uniqueLines.find((entry) =>
      entry.id !== draft.lineId
      && entry.trackingMode === "quantity"
      && ((draft.variationId && entry.variationId === draft.variationId) || (normalizedIsbn && normalizeIsbn(entry.isbn ?? "") === normalizedIsbn) || (normalizedSource && normalizeScannedIdentifier(entry.sourceIdentifier ?? entry.metadata?.barcode ?? "") === normalizedSource))
    );
  };

  const resolveScanIntoBatch = async (
    detectedValue?: string,
    options: { targetBatch?: ReceivingBatch; suppressMessage?: boolean; allowManualReview?: boolean; deferRefresh?: boolean } = {},
  ): Promise<{ saved: boolean; duplicate: boolean; manualReview: boolean; title?: string }> => {
    const value = (detectedValue ?? scanValue).trim();
    if (!value) return { saved: false, duplicate: false, manualReview: false };
    const data = await api<{ resolution: ReceivingResolution }>("/api/admin/receiving/resolve", { method: "POST", body: JSON.stringify({ input: value }) });
    const isbn = data.resolution.kind === "isbn" ? data.resolution.normalized ?? normalizeIsbn(value) : "";
    const resolvedLine = withReceivingDefaults({
      ...line,
      sourceIdentifier: value,
      isbn,
      workTitle: data.resolution.title ?? (data.resolution.kind === "manual" ? value : line.workTitle),
      contributorDisplay: data.resolution.contributorDisplay ?? line.contributorDisplay,
      productType: data.resolution.productType ?? (isbn ? "book" : line.productType),
      variationId: data.resolution.variationId,
      editionId: data.resolution.editionId,
      trackingMode: data.resolution.kind === "store_barcode" ? "copy" : line.trackingMode,
      storeBarcodesText: data.resolution.kind === "store_barcode" ? data.resolution.normalized ?? value : line.storeBarcodesText,
    });
    setScanValue("");
    const warning = data.resolution.warnings[0];
    const actionableWarning = warning === "No local edition matched this ISBN" ? undefined : warning;
    let nextLine = resolvedLine;
    let metadataFound = false;
    if (isbn && isValidIsbn(isbn) && defaults?.receivingMetadataBehavior !== "disabled") {
      const results = await searchMetadata({ isbn });
      const best = bestMetadataForReceiving(isbn, results);
      if (best) {
        metadataFound = true;
        nextLine = withReceivingDefaults(metadataToReceivingLine(nextLine, best));
      }
    }
    if (!isbn && !data.resolution.variationId && defaults?.receivingMetadataBehavior !== "disabled") {
      const results = await searchProductMetadata({ barcode: value });
      const best = results[0];
      if (best) {
        metadataFound = true;
        nextLine = withReceivingDefaults(metadataToReceivingLine(nextLine, best));
      }
    }
    if (!canMutateReceiving) throw new StaffApiError({ status: 403, code: "forbidden", message: "Manager or administrator required" });
    if (isbn && defaults?.receivingMetadataBehavior !== "disabled" && !metadataFound && !data.resolution.variationId) {
      if (options.allowManualReview === false) return { saved: false, duplicate: false, manualReview: true, title: nextLine.workTitle || value };
      setLine(nextLine);
      setItemDialogOpen(true);
      setMessage(actionableWarning ?? "No metadata was found for this ISBN. Review and save the item details manually.");
      return { saved: false, duplicate: false, manualReview: true, title: nextLine.workTitle || value };
    }
    if (!nextLine.workTitle.trim()) {
      if (options.allowManualReview === false) return { saved: false, duplicate: false, manualReview: true, title: value };
      setLine(nextLine);
      setMessage(actionableWarning ?? "No local catalog or metadata match was found. Review and save the item details manually.");
      setItemDialogOpen(true);
      return { saved: false, duplicate: false, manualReview: true, title: value };
    }
    const activeBatch = options.targetBatch ?? (editableBatch ? batch : await createBatch());
    if (!activeBatch) return { saved: false, duplicate: false, manualReview: false, title: nextLine.workTitle };
    const duplicateLine = findDuplicateReceivingLine(activeBatch, nextLine);
    const lineToSave = duplicateLine
      ? receivingLineToDraftLine(duplicateLine, {
        sourceIdentifier: nextLine.sourceIdentifier ?? duplicateLine.sourceIdentifier,
        quantity: duplicateLine.quantity + Math.max(1, nextLine.quantity),
        metadata: duplicateLine.metadata ?? nextLine.metadata,
      })
      : nextLine;
    await savePreparedLine(lineToSave, activeBatch, { deferRefresh: options.deferRefresh });
    if (!options.suppressMessage) setMessage(actionableWarning ?? (duplicateLine ? `Updated ${duplicateLine.workTitle} quantity to ${lineToSave.quantity}` : `Added ${nextLine.workTitle} to ${activeBatch.receivingNumber}`));
    scanRef.current?.focus();
    return { saved: true, duplicate: Boolean(duplicateLine), manualReview: false, title: nextLine.workTitle };
  };
  resolveScanIntoBatchRef.current = resolveScanIntoBatch;

  const resolveScan = (detectedValue?: string) => runMutation("resolve-scan", async () => {
    await resolveScanIntoBatch(detectedValue);
  });

  const handleCameraDetected = (value: string) => {
    setScanValue(value);
    return resolveScan(value);
  };

  const handlePhotoScan = (file?: File | null) => runMutation("photo-scan", async () => {
    if (!file) return;
    setPhotoScanSummary("Reading barcodes from photo...");
    const values = await scanImageFileForBarcodes(file);
    if (!values.length) {
      setPhotoScanSummary("No readable barcodes were found in that photo.");
      setMessage("Try a brighter photo where the barcode blocks are flat and in focus.");
      return;
    }
    const activeBatch = editableBatch ? batch : await createBatch();
    if (!activeBatch) return;
    let savedCount = 0;
    let duplicateCount = 0;
    let reviewCount = 0;
    for (const value of values) {
      const result = await resolveScanIntoBatch(value, { targetBatch: activeBatch, suppressMessage: true, allowManualReview: false });
      if (result?.saved) savedCount += 1;
      if (result?.duplicate) duplicateCount += 1;
      if (result?.manualReview) reviewCount += 1;
    }
    const pieces = [`Photo scan found ${values.length} barcode(s)`, `${savedCount} added or updated`];
    if (duplicateCount) pieces.push(`${duplicateCount} duplicate scan(s) incremented quantity`);
    if (reviewCount) pieces.push(`${reviewCount} need manual review`);
    const summary = `${pieces.join(". ")}.`;
    setPhotoScanSummary(summary);
    setScanValue("");
    setMessage(summary);
    scanRef.current?.focus();
  });

  const startPhoneScanner = () => runMutation("phone-scanner", async () => {
    const activeBatch = editableBatch ? batch : await createBatch();
    if (!activeBatch) return;
    const data = await api<{ session: ScannerSession }>(`/api/admin/receiving/batches/${encodeURIComponent(activeBatch.id)}/scanner-sessions`, {
      method: "POST",
      body: JSON.stringify({ expiresInMinutes: 240 }),
    });
    setPhoneScanner(data.session);
    rememberReceivingRestoreState(activeBatch, data.session);
    setPhoneScannerMessage("Phone scanner is paired. Scans will appear in this batch.");
  });

  const stopPhoneScanner = () => runMutation("phone-scanner-stop", async () => {
    if (!phoneScanner) return;
    await api(`/api/admin/receiving/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/close`, { method: "POST" });
    setPhoneScanner(null);
    rememberReceivingRestoreState(batch, null);
    setPhoneScannerMessage("Phone scanner stopped.");
  });

  const processPhoneScannerQueue = useCallback(async () => {
    if (!phoneScanner || scannerProcessingRef.current) return;
    scannerProcessingRef.current = true;
    try {
      const data = await api<{ scans: ScannerScan[] }>(`/api/admin/receiving/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/scans`);
      if (!data.scans.length) return;
      let targetBatch = activeBatchRef.current?.id === phoneScanner.targetId ? activeBatchRef.current : null;
      if (!targetBatch) {
        const refreshed = await api<{ batch: ReceivingBatch }>(`/api/admin/receiving/batches/${encodeURIComponent(phoneScanner.targetId)}`);
        targetBatch = refreshed.batch;
        setActiveBatchPreservingSavedLines(refreshed.batch);
        setLineReviewBatch(refreshed.batch);
        rememberBatchLines(refreshed.batch.id, refreshed.batch.lines);
      }
      for (const scan of data.scans) {
        if (!scan?.id || !scan.input?.trim()) continue;
        try {
          const result = await resolveScanIntoBatchRef.current?.(scan.input, { targetBatch, suppressMessage: true, allowManualReview: false, deferRefresh: true }) ?? { saved: false, duplicate: false, manualReview: false };
          const status = result.manualReview ? "needs_review" : result.saved ? "processed" : "failed";
          const message = result.manualReview ? `${scan.input} needs manual review on this screen.` : result.saved ? `${scan.input} added from phone.` : `${scan.input} could not be added.`;
          await api(`/api/admin/receiving/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/scans/${encodeURIComponent(scan.id)}/ack`, {
            method: "POST",
            body: JSON.stringify({ status, message }),
          });
          setPhoneScannerMessage(message);
          if (activeBatchRef.current?.id === targetBatch.id) targetBatch = activeBatchRef.current;
          if (result.manualReview) break;
        } catch (caught) {
          const message = staffErrorMessage(caught);
          await api(`/api/admin/receiving/scanner-sessions/${encodeURIComponent(phoneScanner.id)}/scans/${encodeURIComponent(scan.id)}/ack`, {
            method: "POST",
            body: JSON.stringify({ status: "failed", message }),
          }).catch(() => undefined);
          setPhoneScannerMessage(message);
        }
      }
      if (activeBatchRef.current?.id === targetBatch.id) {
        await refreshBatch(targetBatch.id).catch((caught) => setPhoneScannerMessage(staffErrorMessage(caught)));
      }
    } catch (caught) {
      setPhoneScannerMessage(staffErrorMessage(caught));
    } finally {
      scannerProcessingRef.current = false;
    }
  }, [api, phoneScanner, rememberBatchLines, setActiveBatchPreservingSavedLines]);

  useEffect(() => {
    if (!phoneScanner) return undefined;
    void processPhoneScannerQueue();
    const timer = window.setInterval(() => { void processPhoneScannerQueue(); }, 2000);
    return () => window.clearInterval(timer);
  }, [phoneScanner, processPhoneScannerQueue]);

  const searchByTitle = () => runMutation("metadata-search", async () => {
    const title = line.workTitle.trim() || scanValue.trim();
    const results = await searchMetadata({ title, author: line.contributorDisplay || undefined });
    const best = results[0];
    if (!best) {
      if (title && !line.workTitle.trim()) setLine((current) => ({ ...current, workTitle: title }));
      setItemDialogOpen(true);
      setMessage("No metadata was found. Review and save the item details manually.");
      return;
    }
    if (!canMutateReceiving) throw new StaffApiError({ status: 403, code: "forbidden", message: "Manager or administrator required" });
    const nextLine = withReceivingDefaults(metadataToReceivingLine({ ...line, sourceIdentifier: scanValue.trim() || line.sourceIdentifier }, best));
    setScanValue("");
    const activeBatch = editableBatch ? batch : await createBatch();
    await savePreparedLine(nextLine, activeBatch);
    setMessage(`Added ${nextLine.workTitle} to ${activeBatch.receivingNumber}`);
    scanRef.current?.focus();
  });

  const searchMetadata = async (query: { isbn?: string; title?: string; author?: string }): Promise<BookMetadata[]> => {
    const hasQuery = Boolean(query.isbn || query.title?.trim() || query.author?.trim());
    if (!hasQuery) return [];
    const metadata = await api<{ results: BookMetadata[] }>("/api/admin/metadata/search", { method: "POST", body: JSON.stringify(query) });
    if (!metadata.results.length) setMessage("No metadata suggestion returned. Manual receiving remains available.");
    return metadata.results ?? [];
  };

  const searchProductMetadata = async (query: { barcode?: string; title?: string; keywords?: string }): Promise<BookMetadata[]> => {
    const hasQuery = Boolean(query.barcode?.trim() || query.title?.trim() || query.keywords?.trim());
    if (!hasQuery) return [];
    const metadata = await api<{ results: BookMetadata[] }>("/api/admin/metadata/product-search", { method: "POST", body: JSON.stringify(query) });
    if (!metadata.results.length) setMessage("No product suggestion returned. Manual receiving remains available.");
    return metadata.results ?? [];
  };

  const savePreparedLine = async (draft: DraftLine, targetBatch = batch, options: { deferRefresh?: boolean } = {}) => {
    if (!targetBatch || !["draft", "in_progress", "partially_completed", "correction_required"].includes(targetBatch.status)) return;
    if (!canMutateReceiving) throw new StaffApiError({ status: 403, code: "forbidden", message: "Manager or administrator required" });
    const storeBarcodes = draft.storeBarcodesText.split(/\r?\n|,/).map((value) => value.trim()).filter(Boolean);
    const body = {
      lineId: draft.lineId,
      sourceIdentifier: draft.sourceIdentifier,
      isbn: draft.isbn || undefined,
      productType: draft.productType,
      contributorDisplay: draft.contributorDisplay || undefined,
      editionId: draft.editionId,
      variationId: draft.variationId,
      workTitle: draft.workTitle,
      formatCode: draft.formatCode,
      trackingMode: draft.trackingMode,
      quantity: draft.quantity,
      unitCostAmount: draft.unitCostAmount,
      unitSellingPriceAmount: draft.unitSellingPriceAmount,
      currency: defaults?.defaultCurrency ?? storeProfile.currencyCode ?? "USD",
      conditionCode: draft.conditionCode,
      conditionNotes: draft.conditionNotes || undefined,
      locationId: targetBatch.targetLocationId,
      shelfCode: draft.shelfCode || undefined,
      storeBarcodes,
      intendedLabelQuantity: draft.trackingMode === "copy" ? draft.quantity * Math.max(0, defaults?.labelsPerCopy ?? 1) : 0,
      metadata: draft.metadata,
    };
    const data = await api<{ line: ReceivingLine }>(`/api/admin/receiving/batches/${targetBatch.id}/lines`, {
      method: "POST",
      headers: { "idempotency-key": retainedKey("receiving-line", targetBatch.id, JSON.stringify(body)) },
      body: JSON.stringify(body),
    });
    rememberSavedLineImmediately(targetBatch.id, data.line);
    const optimisticBatch = upsertReceivingLine(activeBatchRef.current?.id === targetBatch.id ? activeBatchRef.current : targetBatch, data.line);
    activeBatchRef.current = optimisticBatch;
    setLineReviewBatch(optimisticBatch);
    setBatch(optimisticBatch);
    if (!options.deferRefresh) {
      await refreshBatchUntilLineVisible(targetBatch, data.line);
      queuePostSaveRefresh(targetBatch.id, data.line);
    }
    rememberReceivingRestoreState(optimisticBatch);
    setLine({ ...defaultLine, conditionCode: conditionCodeFromDefault(conditions, defaults?.defaultConditionId) ?? "good", trackingMode: defaults?.defaultTrackingMode ?? "quantity" });
    setItemDialogOpen(false);
    setMessage(data.line.validationErrors.length ? data.line.validationErrors[0] : draft.lineId ? "Item updated in batch" : "Item added to batch");
  };

  const saveLine = () => runMutation("save-line", async () => {
    const activeBatch = editableBatch ? batch : await createBatch();
    await savePreparedLine(line, activeBatch);
    scanRef.current?.focus();
  });

  const editLine = (entry: ReceivingLine) => {
    setLine({
      lineId: entry.id,
      sourceIdentifier: entry.sourceIdentifier,
      editionId: entry.editionId,
      variationId: entry.variationId,
      workTitle: entry.workTitle,
      contributorDisplay: entry.contributorDisplay ?? "",
      isbn: entry.isbn ?? "",
      productType: entry.productType ?? entry.metadata?.productType ?? "book",
      formatCode: entry.formatCode ?? "other",
      trackingMode: entry.trackingMode,
      quantity: entry.quantity,
      unitCostAmount: entry.unitCostAmount,
      unitSellingPriceAmount: entry.unitSellingPriceAmount,
      conditionCode: entry.conditionCode,
      conditionNotes: entry.conditionNotes ?? "",
      shelfCode: entry.shelfCode ?? "",
      storeBarcodesText: Array.isArray((entry.metadata as { storeBarcodes?: unknown[] } | undefined)?.storeBarcodes) ? ((entry.metadata as { storeBarcodes?: string[] }).storeBarcodes ?? []).join("\n") : "",
      metadata: entry.metadata,
    });
    setMessage(`Editing line ${entry.lineNumber}`);
    setItemDialogOpen(true);
  };

  const withReceivingDefaults = (draft: DraftLine): DraftLine => {
    const priceKey = inferImportPriceKey(draft.metadata?.format ?? draft.formatCode);
    const price = appSettings.defaultImportPrices[priceKey] ?? defaultReceivingAppSettings.defaultImportPrices[priceKey];
    const defaultConditionCode = conditionCodeFromDefault(conditions, defaults?.defaultConditionId) ?? "good";
    const defaultPrices = Object.values({ ...defaultReceivingAppSettings.defaultImportPrices, ...appSettings.defaultImportPrices }).map((entry) => entry.amount);
    const shouldApplyFormatPrice = draft.unitSellingPriceAmount <= 0 || defaultPrices.includes(draft.unitSellingPriceAmount);
    return {
      ...draft,
      formatCode: draft.formatCode || inferFormat(draft.metadata?.format, defaults?.defaultFormatId ?? "other"),
      productType: draft.productType ?? draft.metadata?.productType ?? "book",
      conditionCode: draft.conditionCode || defaultConditionCode,
      trackingMode: draft.trackingMode || defaults?.defaultTrackingMode || "quantity",
      unitSellingPriceAmount: shouldApplyFormatPrice ? price.amount : draft.unitSellingPriceAmount,
    };
  };

  const removeLine = (lineId: string) => runMutation("remove-line", async () => {
    if (!batch || !editableBatch) return;
    if (!canMutateReceiving) throw new StaffApiError({ status: 403, code: "forbidden", message: "Manager or administrator required" });
    await api(`/api/admin/receiving/batches/${batch.id}/lines/${lineId}`, { method: "DELETE", headers: { "idempotency-key": crypto.randomUUID() } });
    forgetBatchLine(batch.id, lineId);
    await refreshBatch(batch.id);
    setMessage("Item removed from batch");
  });

  const completeBatch = () => runMutation("complete-batch", async () => {
    const batchToComplete = displayBatch ?? batch;
    if (!batchToComplete || blockingLines.length || !canMutateReceiving) return;
    const fingerprint = JSON.stringify({ batchId: batchToComplete.id, updatedAt: batchToComplete.updatedAt, prepareLabels: true });
    const data = await api<{ result: { batch: ReceivingBatch; labelBatchId?: string; labelError?: string; createdMovements: number; createdCopies: number; requestMatches?: InventoryRequestMatch[] } }>(`/api/admin/receiving/batches/${batchToComplete.id}/complete`, {
      method: "POST",
      body: JSON.stringify({ prepareLabels: true, idempotencyKey: retainedKey("receiving-complete", batchToComplete.id, fingerprint) }),
    });
    setActiveBatch(data.result.batch);
    setLineReviewBatch(data.result.batch);
    setLastCompletion(data.result);
    setRequestMatches(data.result.requestMatches ?? []);
    setCompletionPending(false);
    const completionLabel = data.result.batch.status === "completed" ? "Completed" : "Partially completed";
    clearReceivingRestoreState();
    setMessage(`${completionLabel} ${data.result.batch.receivingNumber}: ${data.result.createdMovements} movement(s), ${data.result.createdCopies} exact copy/copies${data.result.labelBatchId ? ". Label batch prepared for the Labels workspace." : data.result.labelError ? ". Label preparation needs attention." : "."}`);
    await load();
  });

  const recoverBatchStatus = () => runMutation("recover-batch", async () => {
    const batchToRefresh = displayBatch ?? batch;
    if (!batchToRefresh) return;
    await refreshBatch(batchToRefresh.id);
    setMessage("Authoritative receiving batch status refreshed");
  });

  return (
    <section className="page receiving-workspace">
      <header className="page-header">
        <div>
          <p className="eyebrow">Add stock</p>
          <h1>Add Stock</h1>
        </div>
        <div className="actions">
          <button onClick={() => void load()}><RefreshCw /> Refresh</button>
          <div className="setup-menu">
            <button aria-haspopup="dialog" aria-expanded={setupOpen} aria-label="Receiving settings" onClick={() => setSetupOpen((open) => !open)}><Settings /> Settings</button>
            {setupOpen && (
              <div className="setup-menu-panel receive-setup-panel" role="dialog" aria-label="Receiving settings">
                <div className="section-header">
                  <div>
                    <h2>Batch settings</h2>
                    <p>Choose the receiving context before the first scan, or resume a draft batch.</p>
                  </div>
                  <button className="icon" aria-label="Close batch settings" onClick={() => setSetupOpen(false)}><X /></button>
                </div>
                <div className="form-grid compact">
                  <label>Receiving location
                    <select value={targetLocationId} disabled={Boolean(editableBatch)} onChange={(event) => setTargetLocationId(event.target.value)}>
                      <option value="">Choose location</option>
                      {locations.map((location) => <option key={location.id} value={location.id}>{location.name} ({location.code})</option>)}
                    </select>
                  </label>
                  <label>Default condition
                    <select value={line.conditionCode} onChange={(event) => setLine({ ...line, conditionCode: event.target.value })}>
                      {(conditions.length ? conditions : [{ id: "condition-good", code: "good", displayName: "Good", sortOrder: 1, active: true }]).filter((condition) => condition.active).map((condition) => <option key={condition.id} value={condition.code}>{condition.displayName}</option>)}
                    </select>
                  </label>
                  <label className="wide">Batch list
                    <select value={batch?.id ?? ""} onChange={(event) => { if (event.target.value) void resumeBatch(event.target.value); }}>
                      <option value="">Select recent batch</option>
                      {batches.map((entry) => <option key={entry.id} value={entry.id}>{entry.receivingNumber} - {entry.status}</option>)}
                    </select>
                  </label>
                  <div className="wide batch-price-defaults">
                    <h3>Default format pricing</h3>
                    <div className="batch-price-grid">
                      {receivingPriceDefaultOptions.map((option) => (
                        <label key={option.key}>{option.label}
                          <input
                            inputMode="decimal"
                            value={minorUnitsToInput(appSettings.defaultImportPrices[option.key]?.amount ?? 0)}
                            onChange={(event) => setDefaultImportPrice(option.key, event.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="actions">
                  <button onClick={() => setSetupOpen(false)}>Done</button>
                  <button disabled={Boolean(pending) || !targetLocationId || !canMutateReceiving} onClick={saveBatchSettings}><Save /> Save defaults</button>
                  <button className="primary" disabled={Boolean(pending) || !targetLocationId || Boolean(editableBatch)} onClick={startBatch}><PackagePlus /> Start batch</button>
                </div>
                {!canMutateReceiving && <p className="empty-inline">Manager or administrator authority is required to add receiving lines or complete a batch.</p>}
                {batch && <BatchSummary batch={batch} location={activeLocation} />}
              </div>
            )}
          </div>
        </div>
      </header>

      {message && <div className="success" role="status">{message}</div>}
      {error ? <div className="error-banner" role="alert"><AlertTriangle /> {staffErrorMessage(error)}</div> : null}

      <div className="receive-layout">
        <section className="receive-panel wide-panel">
          <header className="section-header">
            <div>
              <h2>Add books</h2>
              <p><Keyboard size={14} /> Alt+R restores scanner focus. Scan or type an ISBN, SKU, or barcode and press Enter. Use camera scan when no hardware scanner is available.</p>
            </div>
          </header>
          <label className="wide scan-input">Scan ISBN, SKU, or store barcode
            <input ref={scanRef} value={scanValue} onChange={(event) => setScanValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void resolveScan(); }} placeholder="Scan, paste, or type then press Enter" />
          </label>
          <div className="actions">
            <button disabled={pending === "resolve-scan"} onClick={() => void resolveScan()}><Search /> Add scanned item</button>
            <button aria-pressed={cameraOpen} disabled={Boolean(pending) && !cameraOpen} onClick={() => setCameraOpen((open) => !open)}><Camera /> {cameraOpen ? "Stop camera" : "Camera scan"}</button>
            {photoScanAvailable && <button disabled={Boolean(pending)} onClick={() => photoScanRef.current?.click()}><Camera /> Photo scan</button>}
            {phoneScannerHostAvailable && (phoneScanner
              ? <button aria-pressed onClick={stopPhoneScanner} disabled={Boolean(pending)}><Smartphone /> Stop phone scanner</button>
              : <button onClick={startPhoneScanner} disabled={Boolean(pending) || !canMutateReceiving || (!editableBatch && !targetLocationId)}><QrCode /> Pair phone scanner</button>)}
            <button disabled={!scanValue.trim() && !line.workTitle.trim() && !line.contributorDisplay.trim()} onClick={searchByTitle}><Search /> Title/author search</button>
            <button onClick={() => setItemDialogOpen(true)}><PackagePlus /> Enter manually</button>
          </div>
          <input ref={photoScanRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(event) => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; void handlePhotoScan(file); }} />
          {photoScanSummary && <p className="empty-inline">{photoScanSummary}</p>}
          {phoneScanner && <PhoneScannerPairingCard session={phoneScanner} message={phoneScannerMessage} />}
          {cameraOpen && <CameraReceivingScanner onDetected={handleCameraDetected} onClose={() => setCameraOpen(false)} />}
        </section>

        <section className="receive-panel wide-panel">
          <header className="section-header">
            <div>
              <h2>Line review</h2>
              <p>{quantitySummary}</p>
            </div>
          </header>
          {displayBatch ? <ReceivingBatchPanel batch={displayBatch} canEdit={Boolean(editableBatch && canMutateReceiving)} onEdit={editLine} onRemove={removeLine} /> : <p className="empty-inline">Scan an item to start a batch automatically, or open Settings to resume one.</p>}
        </section>

        <section className="receive-panel">
          <header className="section-header">
            <div>
              <h2>Completion</h2>
              <p>Completion applies inventory movements and exact-copy creation exactly once through the server.</p>
            </div>
          </header>
          {blockingLines.length > 0 && (
            <div className="receiving-blockers" role="alert">
              <div className="error-banner"><AlertTriangle /> {blockingLines.length} item{blockingLines.length === 1 ? " needs" : "s need"} review before this batch can be completed.</div>
              {blockingLines.map((entry) => (
                <article className="receiving-blocker" key={entry.id}>
                  <div>
                    <strong>Line {entry.lineNumber}: {entry.workTitle}</strong>
                    {receivingLineBlockingIssues(entry).map((issue) => <p key={issue}>{issue}</p>)}
                  </div>
                  {editableBatch && canMutateReceiving && entry.lineStatus !== "completed" && <button onClick={() => editLine(entry)}>Review item</button>}
                </article>
              ))}
            </div>
          )}
          {completionPending ? (
            <div className="confirmation-panel">
              <p>Complete {batch?.receivingNumber} for {activeLocation?.name}? This will apply all ready receiving lines and prepare labels when configured.</p>
              <div className="actions">
                <button className="primary" disabled={!batch || Boolean(blockingLines.length) || Boolean(pending)} onClick={completeBatch}><Check /> Confirm completion</button>
                <button onClick={() => setCompletionPending(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="primary" disabled={!displayBatch || !canMutateReceiving || !(displayBatch.lines ?? []).length || !["draft", "in_progress", "partially_completed", "correction_required"].includes(displayBatch.status) || Boolean(blockingLines.length)} onClick={() => setCompletionPending(true)}><Check /> Review and complete</button>
          )}
          {batch && !["draft", "in_progress", "partially_completed", "correction_required"].includes(batch.status) && <button onClick={recoverBatchStatus}><RefreshCw /> Refresh authoritative status</button>}
          {lastCompletion && <CompletionSummary completion={lastCompletion} onNext={() => { const completedBatchId = (displayBatch ?? batch)?.id; setActiveBatch(null); setLineReviewBatch(null); if (completedBatchId) setSavedLines((current) => { const next = { ...current }; delete next[completedBatchId]; savedLinesRef.current = { ...savedLinesRef.current }; delete savedLinesRef.current[completedBatchId]; return next; }); setLastCompletion(null); clearRetainedScope("receiving-batch"); setMessage("Ready for the next receiving batch"); }} />}
        </section>
      </div>

      <ContextFooter
        items={[
          ["Store", storeProfile.storeDisplayName],
          ["Location", activeLocation ? `${activeLocation.name} (${activeLocation.code})` : "No active receiving location"],
          ["Role", `${String(currentUser.displayName)} - ${String(currentUser.role)}`],
          ["Batch", batch ? `${batch.receivingNumber} ${batch.status}` : "No batch selected"],
        ]}
      />

      {itemDialogOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setItemDialogOpen(false); }}>
          <section className="modal receive-item-dialog" role="dialog" aria-modal="true" aria-labelledby="receive-item-title">
            <header className="section-header">
              <div>
                <h2 id="receive-item-title">{line.lineId ? "Edit item" : "Item to receive"}</h2>
                <p>Review quantity, tracking, condition, shelf, and price before adding this item to the batch.</p>
              </div>
              <button className="icon" aria-label="Close item dialog" onClick={() => setItemDialogOpen(false)}><X /></button>
            </header>
            {line.metadata && (
              <div className="receive-selection-preview">
                <ReceivingCover imageUrl={line.metadata.coverImageUrls[0]} />
                <div>
                  <strong>{line.metadata.title}</strong>
                  <span>{[formatProductType(line.metadata.productType ?? line.productType), line.metadata.authors.join(", ") || "Unknown author", line.metadata.isbn13 ?? line.metadata.isbn10 ?? line.metadata.barcode, line.metadata.publisher, line.metadata.publicationDate].filter(Boolean).join(" / ")}</span>
                  {Boolean(line.metadata.externalLinks?.length) && <ExternalMetadataLinks links={line.metadata.externalLinks ?? []} />}
                  <p>Metadata is attached to this receiving item. The server still owns catalog creation and inventory changes.</p>
                </div>
              </div>
            )}
            <div className="form-grid dialog-form">
              <label>Title<input value={line.workTitle} onChange={(event) => setLine({ ...line, workTitle: event.target.value })} /></label>
              <label>Author<input value={line.contributorDisplay} onChange={(event) => setLine({ ...line, contributorDisplay: event.target.value })} /></label>
              <label>ISBN<input value={line.isbn} onChange={(event) => setLine({ ...line, isbn: normalizeIsbn(event.target.value) })} /></label>
              <label>Product type<select value={line.productType} onChange={(event) => setLine({ ...line, productType: event.target.value as ProductType })}>{productTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
              <label>Tracking<select value={line.trackingMode} onChange={(event) => setLine({ ...line, trackingMode: event.target.value as "copy" | "quantity" })}><option value="quantity">Quantity inventory</option><option value="copy">Exact copies</option></select></label>
              <label>Quantity<input type="number" min={1} value={line.quantity} onChange={(event) => setLine({ ...line, quantity: Number(event.target.value) })} /></label>
              <label>Cost<input inputMode="decimal" value={minorUnitsToInput(line.unitCostAmount)} onChange={(event) => setLine({ ...line, unitCostAmount: inputToMinorUnits(event.target.value) })} /></label>
              <label>Price<input inputMode="decimal" value={minorUnitsToInput(line.unitSellingPriceAmount)} onChange={(event) => setLine({ ...line, unitSellingPriceAmount: inputToMinorUnits(event.target.value) })} /></label>
              <label>Format code<input value={line.formatCode} onChange={(event) => setLine({ ...line, formatCode: event.target.value })} /></label>
              <label>Shelf<select value={line.shelfCode} onChange={(event) => setLine({ ...line, shelfCode: event.target.value })}><option value="">No shelf</option>{shelves.filter((shelf) => shelf.active && (!targetLocationId || shelf.locationId === targetLocationId)).map((shelf) => <option key={shelf.id} value={shelf.code}>{shelf.code}{shelf.name ? ` - ${shelf.name}` : ""}</option>)}</select></label>
              <label className="wide">Condition notes<textarea value={line.conditionNotes} onChange={(event) => setLine({ ...line, conditionNotes: event.target.value })} /></label>
              {line.trackingMode === "copy" && <label className="wide">Exact-copy barcodes<textarea value={line.storeBarcodesText} onChange={(event) => setLine({ ...line, storeBarcodesText: event.target.value })} placeholder="One provided barcode per copy, or leave blank for server allocation" /></label>}
            </div>
            <div className="actions">
              {line.lineId && <button onClick={() => setLine(defaultLine)}><X /> Clear edit</button>}
              <button onClick={() => setItemDialogOpen(false)}>Cancel</button>
              <button className="primary" disabled={!canSaveDialogLine} onClick={saveLine}><Save /> {line.lineId ? "Update item" : "Add to batch"}</button>
            </div>
          </section>
        </div>
      )}
      {requestMatches.length > 0 && (
        <RequestMatchDialog
          matches={requestMatches}
          onClose={() => setRequestMatches([])}
          onMarkReceived={async (match) => {
            const data = await api<{ request: RequestMatchUpdate }>(`/api/pos/customer-requests/${encodeURIComponent(match.requestId)}`, {
              method: "PATCH",
              body: JSON.stringify({ status: "received" }),
            });
            setRequestMatches((current) => current.map((entry) => entry.requestId === match.requestId ? { ...entry, status: data.request.status } : entry));
          }}
        />
      )}
    </section>
  );
}

function readReceivingRestoreState(): ReceivingRestoreState | null {
  const raw = localStorage.getItem(receivingRestoreKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ReceivingRestoreState;
    if (!parsed.batchId && !parsed.scanner?.targetId) return null;
    return parsed;
  } catch {
    localStorage.removeItem(receivingRestoreKey);
    return null;
  }
}

function PhoneScannerPairingCard({ session, message }: { session: ScannerSession; message?: string }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?${new URLSearchParams({ size: "220x220", data: session.url })}`;
  return (
    <div className="phone-pairing-card">
      <img src={qrUrl} alt="Phone scanner pairing QR code" />
      <div>
        <strong>Phone scanner paired</strong>
        <span>Open the camera on a phone, scan this QR code, and keep scanning book barcodes.</span>
        <small>Expires {new Date(session.expiresAt).toLocaleTimeString()}</small>
        {message && <p>{message}</p>}
      </div>
    </div>
  );
}

function isPhoneLikeDevice(): boolean {
  const userAgent = navigator.userAgent;
  const isPhoneUserAgent = /Android.*Mobile|iPhone|iPod/i.test(userAgent);
  const isSmallCoarsePointer = window.matchMedia?.("(max-width: 767px) and (pointer: coarse)").matches ?? false;
  return isPhoneUserAgent || isSmallCoarsePointer;
}

function ReceivingCover({ imageUrl }: { imageUrl?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [imageUrl]);
  return imageUrl && !failed
    ? <img className="cover" src={imageUrl} alt="Cover art" onError={() => setFailed(true)} />
    : <div className="cover placeholder"><BookOpen aria-hidden /></div>;
}

function CameraReceivingScanner({ onDetected, onClose }: { onDetected: (value: string) => Promise<void> | void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const onDetectedRef = useRef(onDetected);
  const processingRef = useRef(false);
  const lastScanRef = useRef<{ value: string; scannedAt: number } | null>(null);
  const [message, setMessage] = useState("Starting camera...");

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    let active = true;
    const stop = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      controlsRef.current?.stop();
      controlsRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
    const completeScan = async (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return false;
      const normalizedIsbn = normalizeIsbn(trimmed);
      const value = isValidIsbn(normalizedIsbn) ? normalizedIsbn : trimmed;
      const lastScan = lastScanRef.current;
      const now = Date.now();
      if (processingRef.current || (lastScan?.value === value && now - lastScan.scannedAt < 1400)) return false;
      processingRef.current = true;
      lastScanRef.current = { value, scannedAt: now };
      setMessage(`Scanned ${value}. Adding to batch...`);
      try {
        await onDetectedRef.current(value);
        if (active) setMessage("Added scan. Keep the camera on the next barcode.");
      } catch (error) {
        if (active) setMessage(error instanceof Error ? error.message : "Could not add scanned barcode.");
      } finally {
        processingRef.current = false;
      }
      return true;
    };

    const startZxingFallback = async () => {
      const video = videoRef.current;
      if (!video) return;
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        setMessage("Point the camera at the book barcode.");
        controlsRef.current = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } }, audio: false },
          video,
          (result) => {
            if (!active || !result) return;
            void completeScan(result.getText());
          },
        );
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera barcode scanning.");
      }
    };

    const start = async () => {
      const Detector = (globalThis as typeof globalThis & { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
      if (!navigator.mediaDevices?.getUserMedia) {
        setMessage("Camera access is not available in this browser.");
        return;
      }
      if (!Detector) {
        await startZxingFallback();
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        const detector = new Detector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"] });
        setMessage("Point the camera at the book barcode.");

        const detect = async () => {
          if (!active || !videoRef.current) return;
          try {
            const [barcode] = await detector.detect(videoRef.current);
            if (barcode?.rawValue) await completeScan(barcode.rawValue);
          } catch {
            setMessage("Looking for a readable barcode...");
          }
          frameRef.current = requestAnimationFrame(() => { void detect(); });
        };
        void detect();
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera.");
      }
    };

    void start();
    return () => {
      active = false;
      stop();
    };
  }, []);

  return (
    <div className="scanner-panel" role="dialog" aria-label="Camera barcode scanner">
      <div className="scanner-header">
        <strong>Camera barcode scan</strong>
        <button className="icon" aria-label="Close camera scanner" onClick={onClose}><X /></button>
      </div>
      <div className="scanner-video-wrap">
        <video ref={videoRef} className="scanner-video" muted playsInline />
        <div className="scanner-target" aria-hidden />
      </div>
      <p>{message}</p>
    </div>
  );
}

function ContextFooter({ items }: { items: [string, string][] }) {
  return (
    <footer className="context-footer" aria-label="Page context">
      {items.map(([label, value]) => <span key={label}><strong>{label}:</strong> {value}</span>)}
    </footer>
  );
}

function ReceivingBatchPanel({ batch, canEdit, onEdit, onRemove }: { batch: ReceivingBatch; canEdit: boolean; onEdit: (line: ReceivingLine) => void; onRemove: (lineId: string) => void }) {
  const lines = (batch.lines ?? []).map(normalizeReceivingLineForDisplay);
  return (
    <section className="receiving-list">
      {lines.map((entry) => {
        const blockingIssues = receivingLineBlockingIssues(entry);
        return <article className={`receive-row ${blockingIssues.length ? "conflict" : ""}`} key={entry.id}>
        <ReceivingCover imageUrl={entry.metadata?.coverImageUrls[0]} />
        <div>
          <div className="receive-row-header">
            <div>
              <h3>{entry.workTitle}</h3>
              <p>{[entry.contributorDisplay, entry.isbn, entry.variationId ? "Existing local match" : "New or unresolved local catalog"].filter(Boolean).join(" / ")}</p>
            </div>
            <span className="badge">{entry.lineStatus}</span>
          </div>
          <dl className="metadata-fields">
            <dt>Tracking</dt><dd>{entry.trackingMode}</dd>
            <dt>Qty</dt><dd>{entry.quantity}</dd>
            <dt>Cost</dt><dd>{formatCents(entry.unitCostAmount, entry.currency)}</dd>
            <dt>Price</dt><dd>{formatCents(entry.unitSellingPriceAmount, entry.currency)}</dd>
            <dt>Condition</dt><dd>{entry.conditionCode}</dd>
            <dt>Location</dt><dd>{entry.locationId}</dd>
          </dl>
          {blockingIssues.map((error) => <p className="square-match-note error-text" key={error}>{error}</p>)}
          {entry.validationWarnings.map((warning) => <p className="square-match-note" key={warning}>{warning}</p>)}
          {canEdit && entry.lineStatus !== "completed" && <div className="actions"><button onClick={() => onEdit(entry)}>Edit</button><button onClick={() => onRemove(entry.id)}>Remove</button></div>}
        </div>
      </article>})}
      {!lines.length && <p className="empty-inline">No lines yet. Scan or search to start the draft.</p>}
    </section>
  );
}

function receivingLineBlockingIssues(line: ReceivingLine): string[] {
  if (["ready", "completed"].includes(line.lineStatus)) return line.validationErrors;
  if (line.validationErrors.length) return line.validationErrors;
  if (line.lineStatus === "draft") return ["This item is still a draft. Review it and save the required details."];
  if (line.lineStatus === "failed") return ["This item failed during an earlier completion attempt. Review and save it before trying again."];
  if (line.lineStatus === "cancelled") return ["This item is cancelled. Remove it from the batch or add it again."];
  if (line.lineStatus === "corrected") return ["This corrected item must be reviewed and saved before completion."];
  return [`This item has a ${line.lineStatus} status and is not ready for completion.`];
}

function BatchSummary({ batch, location }: { batch: ReceivingBatch; location?: InventoryLocation }) {
  return (
    <dl className="metadata-fields">
      <dt>Number</dt><dd>{batch.receivingNumber}</dd>
      <dt>Status</dt><dd>{batch.status}</dd>
      <dt>Location</dt><dd>{location?.name ?? batch.targetLocationId}</dd>
      <dt>Lines</dt><dd>{batch.itemTotal}</dd>
      <dt>Quantity units</dt><dd>{batch.quantityTotal}</dd>
      <dt>Exact copies</dt><dd>{batch.copyTotal}</dd>
      <dt>Cost total</dt><dd>{formatCents(batch.costTotalAmount, "USD")}</dd>
    </dl>
  );
}

function CompletionSummary({ completion, onNext }: { completion: { batch: ReceivingBatch; createdMovements: number; createdCopies: number; labelBatchId?: string; labelError?: string }; onNext: () => void }) {
  const complete = completion.batch.status === "completed";
  return (
    <div className="completion-panel">
      <h3>{completion.batch.receivingNumber} {complete ? "completed" : "partially completed"}</h3>
      <p>{completion.createdMovements} movement(s), {completion.createdCopies} exact copy/copies. Inventory confirmation comes from the completed batch response.</p>
      {completion.labelBatchId && <p>Label batch prepared for the Labels workspace: {completion.labelBatchId}</p>}
      {completion.labelError && <p className="warning">{completion.labelError}</p>}
      {complete && <button onClick={onNext}>Start next batch</button>}
    </div>
  );
}

function RequestMatchDialog({ matches, onClose, onMarkReceived }: { matches: InventoryRequestMatch[]; onClose: () => void; onMarkReceived: (match: InventoryRequestMatch) => Promise<void> }) {
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const markReceived = async (match: InventoryRequestMatch) => {
    setPendingRequestId(match.requestId);
    setError("");
    try {
      await onMarkReceived(match);
    } catch {
      setError("Could not mark the request received. Try again from the Requests page.");
    } finally {
      setPendingRequestId(null);
    }
  };
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal request-match-dialog" role="dialog" aria-modal="true" aria-labelledby="request-match-title">
        <header className="section-header">
          <div>
            <p className="eyebrow">Requested book</p>
            <h2 id="request-match-title">{matches.length === 1 ? "This book was requested" : "These books were requested"}</h2>
          </div>
          <button className="icon" aria-label="Close request notification" onClick={onClose}><X /></button>
        </header>
        <div className="settings-list">
          {matches.map((match) => (
            <article key={match.requestId}>
              <strong>{match.title}</strong>
              <span>{match.customerDisplayName ?? "Customer request"}{match.author ? ` / ${match.author}` : ""}</span>
              <span>Matched by {formatRequestMatchType(match.matchedBy)}{match.isbn ? ` / ${match.isbn}` : ""} / {match.status}</span>
              {match.status === "received"
                ? <span className="status-ok">Marked received</span>
                : <button onClick={() => void markReceived(match)} disabled={pendingRequestId === match.requestId}><Check /> {pendingRequestId === match.requestId ? "Marking..." : "Mark request received"}</button>}
            </article>
          ))}
        </div>
        {error && <p className="error-text">{error}</p>}
        <div className="modal-actions">
          <button className="primary" onClick={onClose}><Check /> OK</button>
        </div>
      </section>
    </div>
  );
}

function formatRequestMatchType(value: InventoryRequestMatch["matchedBy"]): string {
  if (value === "isbn") return "ISBN";
  if (value === "title_author") return "title and author";
  return "title";
}

function LabelControls({ template, batch, startingPosition, scale, onStartingPosition, onScale, onPreview, onPrint, disabled }: { template?: LabelTemplate; batch?: LabelBatch; startingPosition: number; scale: number; onStartingPosition: (value: number) => void; onScale: (value: number) => void; onPreview: () => void; onPrint: () => void; disabled: boolean }) {
  if (!template) {
    return <p className="empty-inline">No active label template is configured. An administrator can configure templates in Settings or Labels.</p>;
  }
  return (
    <div className="label-tools">
      <dl className="metadata-fields">
        <dt>Template</dt><dd>{template.name}</dd>
        <dt>Sheet</dt><dd>{template.pageWidthMm} x {template.pageHeightMm} mm</dd>
        <dt>Label</dt><dd>{template.labelWidthMm} x {template.labelHeightMm} mm</dd>
        <dt>Grid</dt><dd>{template.rows} rows x {template.columns} columns</dd>
        <dt>Batch</dt><dd>{batch ? `${batch.id} (${batch.status})` : "None prepared"}</dd>
      </dl>
      <label>Starting label position<input type="number" min={1} value={startingPosition} onChange={(event) => onStartingPosition(Number(event.target.value))} /></label>
      <label>Preview scale %<input type="number" min={50} max={150} value={scale} onChange={(event) => onScale(Number(event.target.value))} /></label>
      <div className="calibration-preview" style={{ width: `${Math.max(40, template.labelWidthMm * scale / 100)}mm`, minHeight: `${Math.max(18, template.labelHeightMm * scale / 100)}mm` }}>
        <strong>Calibration</strong>
        <span>Use actual size / 100% in the browser print dialog.</span>
      </div>
      <div className="actions">
        <button disabled={disabled} onClick={onPreview}><FileText /> Preview labels</button>
        <button disabled={disabled} onClick={onPrint}><Printer /> Open browser print</button>
      </div>
    </div>
  );
}

function LabelSheetPreview({ documentUrl, template, scale }: { documentUrl: string; template?: LabelTemplate; scale: number }) {
  if (!template) return null;
  const safeScale = Math.min(150, Math.max(40, Number.isFinite(scale) ? scale : 100));
  const scaleRatio = safeScale / 100;
  const sheetWidthPx = mmToCssPx(template.pageWidthMm);
  const sheetHeightPx = mmToCssPx(template.pageHeightMm);
  return (
    <div className="label-preview-scroll" style={{ minHeight: `${Math.min(sheetHeightPx * scaleRatio + 24, 760)}px` }}>
      <div className="label-preview-sheet" style={{ width: `${sheetWidthPx * scaleRatio}px`, height: `${sheetHeightPx * scaleRatio}px` }}>
        <iframe
          className="label-preview-frame"
          title="Label sheet preview"
          src={documentUrl}
          style={{
            width: `${sheetWidthPx}px`,
            height: `${sheetHeightPx}px`,
            transform: `scale(${scaleRatio})`,
          }}
        />
      </div>
    </div>
  );
}

export function LabelsPage({ api, apiText, storeProfile, currentUser }: { api: ApiClient; apiText: ApiClient; storeProfile: PublicStoreProfile; currentUser: UserSession }) {
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [batches, setBatches] = useState<LabelBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<LabelBatch | null>(null);
  const [inventoryItems, setInventoryItems] = useState<LabelInventoryItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<LabelInventoryItem[]>([]);
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<"recent" | "week" | "month" | "all">("recent");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [startingPosition, setStartingPosition] = useState(1);
  const [scale, setScale] = useState(100);
  const [labelStep, setLabelStep] = useState<LabelWizardStep>("find");
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState("");
  const canCreateBatch = currentUser.role === "administrator" || currentUser.role === "manager";

  const load = useCallback(async () => {
    setError(null);
    const [templateData, batchData] = await Promise.all([
      api<{ templates: LabelTemplate[] }>("/api/admin/labels/templates"),
      api<{ batches: LabelBatch[] }>("/api/admin/labels/batches"),
    ]);
    setTemplates(templateData.templates ?? []);
    setBatches(batchData.batches ?? []);
    setSelectedTemplateId((current) => current || templateData.templates.find((template) => template.active)?.id || templateData.templates[0]?.id || "");
  }, [api]);
  useEffect(() => { void load().catch((caught) => setError(caught)); }, [load]);

  const run = async (label: string, task: () => Promise<void>) => {
    setPending(label);
    setError(null);
    setMessage("");
    try {
      await task();
    } catch (caught) {
      setError(caught);
    } finally {
      setPending("");
    }
  };

  const searchInventory = async (mode: "manual" | "preset" = "manual", nextFilter: "recent" | "week" | "month" | "all" = activeFilter) => run("search", async () => {
    const manualQuery = mode === "manual" ? query.trim() : "";
    const params = new URLSearchParams({ filter: manualQuery ? "all" : nextFilter, limit: "60" });
    if (manualQuery) params.set("q", manualQuery);
    const data = await api<{ items: LabelInventoryItem[] }>(`/api/admin/labels/inventory?${params}`);
    if (mode === "preset") {
      setQuery("");
      setActiveFilter(nextFilter);
    }
    setInventoryItems(data.items ?? []);
    const label = manualQuery ? `matching "${manualQuery}"` : filterLabel(nextFilter).toLowerCase();
    setMessage(data.items.length ? `${data.items.length} labelable inventory item(s) found ${label}.` : `No labelable inventory found ${label}.`);
  });

  const readBatch = async (batchId: string) => {
    try {
      const data = await api<{ batch: LabelBatch }>(`/api/admin/labels/batches/${batchId}`);
      setSelectedBatch(data.batch);
      await previewBatch(data.batch.id);
      setLabelStep("preview");
    } catch (caught) {
      setError(caught);
    }
  };

  const previewBatch = async (batchId: string) => {
    try {
      const params = new URLSearchParams({ startingPosition: String(startingPosition), t: String(Date.now()) });
      const path = `/api/admin/labels/batches/${encodeURIComponent(batchId)}/preview?${params}`;
      await apiText<string>(path);
      setPreviewDocumentUrl(path);
      setMessage("Preview loaded from the configured label sheet.");
    } catch (caught) {
      setError(caught);
    }
  };

  const printBatch = async (batchId: string) => {
    try {
      await openPrintHtml(apiText, batchId, startingPosition);
      setMessage(`Browser print dialog opened. ${productBrand.name} does not mark labels as physically printed.`);
      await load();
    } catch (caught) {
      setError(caught);
    }
  };

  const createInventoryBatch = async () => run("batch", async () => {
    if (!selectedTemplateId) throw new StaffApiError({ status: 409, code: "label_template_required", message: "Choose a label sheet before creating a batch" });
    if (!selectedItems.length) throw new StaffApiError({ status: 409, code: "label_items_required", message: "Add at least one inventory item to the print queue" });
    const inventorySelections = selectedItems.map(labelInventorySelection);
    const fingerprint = JSON.stringify({ templateId: selectedTemplateId, entries: inventorySelections });
    const data = await api<{ batch: LabelBatch }>("/api/admin/labels/batches", {
      method: "POST",
      body: JSON.stringify({
        sourceType: "inventory",
        inventorySelections,
        templateId: selectedTemplateId,
        idempotencyKey: retainedKey("label-inventory-batch", selectedTemplateId, fingerprint),
      }),
    });
    setSelectedBatch(data.batch);
    setSelectedItems([]);
    setPreviewDocumentUrl("");
    setLabelStep("preview");
    await previewBatch(data.batch.id);
    setMessage(`Created label batch with ${data.batch.labelCount} label(s).`);
    await load();
  });

  const activeTemplate = templates.find((template) => template.id === selectedTemplateId) ?? templates.find((template) => template.active) ?? templates[0];
  const totalPositions = activeTemplate ? activeTemplate.rows * activeTemplate.columns : 0;
  const selectedIds = new Set(selectedItems.map(labelInventoryKey));
  const queuedLabelCount = selectedItems.reduce((sum, item) => sum + labelInventoryCount(item), 0);
  const previewEntries = selectedBatch?.entries ?? selectedItems.map((item, index) => ({
    id: labelInventoryKey(item),
    labelBatchId: "queued",
    sequence: index + 1,
    ...labelInventoryPreviewEntry(item),
    renderStatus: "ready" as const,
  }));

  return (
    <section className="page labels-workspace">
      <header className="page-header">
        <div>
          <p className="eyebrow">Labels</p>
          <h1>Label creator</h1>
        </div>
      </header>
      {message && <div className="success">{message}</div>}
      {error ? <div className="error-banner"><AlertTriangle /> {staffErrorMessage(error)}</div> : null}
      <ol className="trade-stepper label-stepper" aria-label="Label creation progress">
        <li className={labelStep === "find" ? "active" : ""}><span>1</span>Find inventory</li>
        <li className={labelStep === "queue" ? "active" : ""}><span>2</span>Build queue</li>
        <li className={labelStep === "preview" ? "active" : ""}><span>3</span>Preview & print</li>
      </ol>

      {labelStep === "find" && <section className="label-creator-grid label-wizard">
        <div className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Build</p>
              <h2>Find labelable items</h2>
            </div>
          </div>
          <div className="label-search-tools">
            <label className="searchbox"><Search /><span className="sr-only">Search label inventory</span><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void searchInventory("manual"); }} placeholder="Search title, author, ISBN, SKU, barcode" /></label>
            <button disabled={Boolean(pending) || !query.trim()} onClick={() => void searchInventory("manual")}><Search /> Search items</button>
          </div>
          <div className="label-filter-buttons" aria-label="Quick label searches">
            {(["recent", "week", "month", "all"] as const).map((option) => (
              <button key={option} className={activeFilter === option && !query.trim() ? "active" : ""} disabled={Boolean(pending)} onClick={() => void searchInventory("preset", option)}>
                {filterLabel(option)}
              </button>
            ))}
          </div>
          <div className="label-source-list">
            {inventoryItems.map((item) => (
              <article className="label-source-row" key={labelInventoryKey(item)}>
                <div className="cover placeholder">{item.trackingMode === "quantity" ? "Qty" : "Copy"}</div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{[item.contributorDisplay, item.format, item.condition, item.storeBarcode ? `Barcode ${item.storeBarcode}` : item.inventoryNumber ? `SKU ${item.inventoryNumber}` : undefined].filter(Boolean).join(" / ")}</p>
                  <span>{[item.trackingMode === "quantity" ? `${labelInventoryCount(item)} available label(s)` : "Exact-copy label", item.location, item.shelf, item.createdAt ? `Added ${new Date(item.createdAt).toLocaleDateString()}` : undefined].filter(Boolean).join(" / ")}</span>
                </div>
                <strong>{formatCents(item.sellingPriceAmount, item.currency)}</strong>
                <button disabled={selectedIds.has(labelInventoryKey(item))} onClick={() => setSelectedItems((items) => items.some((entry) => labelInventoryKey(entry) === labelInventoryKey(item)) ? items : [...items, item])}>Add to queue</button>
              </article>
            ))}
            {!inventoryItems.length && <p className="empty compact-empty">Search by ISBN, title, author, SKU, or barcode, or use a quick search for added inventory. Quantity stock adds one label per available unit; exact copies add one label per copy barcode.</p>}
          </div>
        </div>

        <aside className="panel label-list-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Queue</p>
              <h2>Current label list</h2>
            </div>
          </div>
          <div className="label-selection-list">
            <h3>Print queue ({queuedLabelCount})</h3>
            {selectedItems.map((item) => (
              <div className="label-selection-row" key={labelInventoryKey(item)}>
                <span>{item.title}<small>{labelInventoryCount(item)} label(s) / {item.storeBarcode ?? item.inventoryNumber ?? item.variationId}</small></span>
                <button aria-label={`Remove label for ${item.title}`} onClick={() => setSelectedItems((items) => items.filter((entry) => labelInventoryKey(entry) !== labelInventoryKey(item)))}><X /> Remove</button>
              </div>
            ))}
            {!selectedItems.length && <p className="empty-inline">Search results can be added here one at a time.</p>}
          </div>
          <button className="primary" disabled={!selectedItems.length} onClick={() => setLabelStep("queue")}>Continue to sheet setup</button>
        </aside>
      </section>}

      {labelStep === "queue" && <section className="label-creator-grid label-wizard">
        <div className="panel label-list-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Sheet</p>
              <h2>Arrange labels</h2>
            </div>
          </div>
          <label>Avery-style sheet
            <select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
              <option value="">Choose a configured sheet</option>
              {templates.filter((template) => template.active).map((template) => (
                <option key={template.id} value={template.id}>{template.name} - {template.labelWidthMm} x {template.labelHeightMm} mm, {template.rows} rows x {template.columns} columns</option>
              ))}
            </select>
          </label>
          {activeTemplate ? (
            <div className="label-sheet-summary">
              <span><strong>Page</strong>{activeTemplate.pageWidthMm} x {activeTemplate.pageHeightMm} mm</span>
              <span><strong>Labels</strong>{activeTemplate.rows} rows x {activeTemplate.columns} columns</span>
              <span><strong>Capacity</strong>{totalPositions} positions</span>
              <span><strong>Starting at</strong>Position {startingPosition}</span>
            </div>
          ) : <p className="empty-inline">No active label sheet is configured. Administrators can add one in Settings.</p>}
          <div className="label-selection-list">
            <h3>Print queue ({queuedLabelCount})</h3>
            {selectedItems.map((item) => (
              <div className="label-selection-row" key={labelInventoryKey(item)}>
                <span>{item.title}<small>{labelInventoryCount(item)} label(s) / {item.storeBarcode ?? item.inventoryNumber ?? item.variationId}</small></span>
                <button aria-label={`Remove label for ${item.title}`} onClick={() => setSelectedItems((items) => items.filter((entry) => labelInventoryKey(entry) !== labelInventoryKey(item)))}><X /> Remove</button>
              </div>
            ))}
            {!selectedItems.length && <p className="empty-inline">Add searched items here before creating a batch.</p>}
          </div>
          <button className="primary" disabled={!canCreateBatch || !selectedItems.length || !activeTemplate || Boolean(pending)} onClick={() => void createInventoryBatch()}><FileText /> Create label batch</button>
          {!canCreateBatch && <p className="empty-inline">Manager or administrator authority is required to create a label batch.</p>}
          <div className="actions">
            <button onClick={() => setLabelStep("find")}><Search /> Add more items</button>
            <button disabled={!selectedBatch} onClick={() => setLabelStep("preview")}><FileText /> Go to preview</button>
          </div>
        </div>
        <aside className="panel">
          <div className="label-preview-panel" aria-live="polite">
            <h3>Queue preview</h3>
            {previewEntries.length ? previewEntries.map((entry) => (
              <div className="label-preview-card" key={entry.id}>
                <strong>{entry.title}</strong>
                <span>{[entry.contributorDisplay, entry.format, entry.condition].filter(Boolean).join(" / ")}</span>
                <small>{entry.labelCount} label(s){entry.storeBarcode ? ` / barcode ${entry.storeBarcode}` : " / no barcode configured"}</small>
              </div>
            )) : <p className="empty-inline">Preview appears here as labels are queued or a batch is selected.</p>}
          </div>
        </aside>
      </section>}

      {labelStep === "preview" && <section className="label-creator-grid label-wizard">
        <div className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Preview</p>
              <h2>Review labels before printing</h2>
            </div>
          </div>
          <LabelControls template={activeTemplate} batch={selectedBatch ?? undefined} startingPosition={startingPosition} scale={scale} onStartingPosition={setStartingPosition} onScale={setScale} onPreview={() => { if (selectedBatch) void previewBatch(selectedBatch.id); }} onPrint={() => { if (selectedBatch) void printBatch(selectedBatch.id); }} disabled={!selectedBatch} />
          {previewDocumentUrl ? <LabelSheetPreview documentUrl={previewDocumentUrl} template={activeTemplate} scale={scale} /> : <p className="empty compact-empty">Create or select a label batch, then use Preview labels to render the configured sheet here.</p>}
        </div>
        <aside className="panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Batch</p>
              <h2>Selected labels</h2>
            </div>
          </div>
          {selectedBatch?.entries ? selectedBatch.entries.map((entry) => (
            <div className="label-preview-card" key={entry.id}>
              <strong>{entry.title}</strong>
              <span>{[entry.contributorDisplay, entry.condition, formatCents(entry.sellingPriceAmount, entry.currency), entry.storeBarcode].filter(Boolean).join(" / ")}</span>
              <small>{entry.labelCount} label(s)</small>
            </div>
          )) : <p className="empty-inline">No selected batch yet.</p>}
          <div className="actions">
            <button onClick={() => setLabelStep("find")}><Search /> Find more inventory</button>
            <button onClick={() => setLabelStep("queue")}><Tags /> Queue</button>
          </div>
        </aside>
      </section>}

      {labelStep === "preview" && <section className="label-history">
        <div className="section-heading">
          <div>
            <p className="eyebrow">History</p>
            <h2>Label batches</h2>
          </div>
        </div>
        <div className="split">
          <section className="table-wrap">
          <table>
            <thead><tr><th>Batch</th><th>Status</th><th>Source</th><th>Labels</th><th /></tr></thead>
            <tbody>{batches.map((batch) => <tr key={batch.id}><td>{batch.id}</td><td>{batch.status}</td><td>{batch.sourceType}</td><td>{batch.labelCount}</td><td><button onClick={() => void readBatch(batch.id)}><FileText /> Preview</button><button onClick={() => void printBatch(batch.id)}><Printer /> Print</button></td></tr>)}</tbody>
          </table>
          {!batches.length && <p className="empty-inline">No label batches are ready yet. Completed copy receiving can prepare labels when a default template is configured.</p>}
          </section>
          <aside className="panel">
            <h2>Configured sheets</h2>
            {templates.map((template) => <p key={template.id}>{template.name} {template.active ? "" : "(archived)"} - {template.pageWidthMm} x {template.pageHeightMm} mm page, {template.rows} rows x {template.columns} columns</p>)}
          </aside>
        </div>
      </section>}
      <ContextFooter items={[["Store", storeProfile.storeDisplayName], ["Role", String(currentUser.role)], ["Print mode", "Browser dialog only"]]} />
    </section>
  );
}

export function InventorySettingsPage({ api, shellStoreProfile, currentUser, theme, onThemeChange, onNavigate, organizationModules = [], onModulesChanged }: { api: ApiClient; shellStoreProfile: PublicStoreProfile; currentUser: UserSession; theme: ThemeMode; onThemeChange: (theme: ThemeMode) => void; onNavigate?: (path: string) => void; organizationModules?: OrganizationModuleView[]; onModulesChanged?: (modules: OrganizationModuleView[]) => void }) {
  const [data, setData] = useState<SettingsData | null>(null);
  const [storeProfile, setStoreProfile] = useState<PublicStoreProfile | null>(null);
  const [locationDraft, setLocationDraft] = useState<LocationDraft>(emptyLocationDraft);
  const [shelfDraft, setShelfDraft] = useState<ShelfDraft>(emptyShelfDraft);
  const [formatDraft, setFormatDraft] = useState<FormatDraft>(emptyFormatDraft);
  const [conditionDraft, setConditionDraft] = useState<ConditionDraft>(emptyConditionDraft);
  const [appSettings, setAppSettings] = useState<Record<string, unknown>>({});
  const [tradeInPolicy, setTradeInPolicy] = useState<TradeInPolicySettings>(defaultTradeInPolicySettings);
  const [themeDraft, setThemeDraft] = useState<ThemeMode>(theme);
  const [loadedTradeInCreditMode, setLoadedTradeInCreditMode] = useState<TradeInCreditMode>(defaultTradeInPolicySettings.creditMode);
  const [tradeInConversionInput, setTradeInConversionInput] = useState("");
  const [supplierProviders, setSupplierProviders] = useState<SupplierSearchProvider[]>(normalizeSupplierProviders());
  const [supplierDiagnostics, setSupplierDiagnostics] = useState<SupplierSearchDiagnostics | null>(null);
  const [labelStyle, setLabelStyle] = useState<LabelStyleSettings>(defaultLabelStyleSettings);
  const [discounts, setDiscounts] = useState<PosDiscountDefinition[]>([]);
  const [discountDraft, setDiscountDraft] = useState<DiscountDraft>(emptyDiscountDraft);
  const [moduleViews, setModuleViews] = useState<OrganizationModuleView[]>(organizationModules);
  const [moduleReason, setModuleReason] = useState("");
  const [modulePendingKey, setModulePendingKey] = useState<ModuleKey | "">("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState("");
  const [activeSettingsSection, setActiveSettingsSection] = useState<SettingsSectionId | null>(null);
  const canAdmin = currentUser.role === "administrator";
  const canManageTradeIns = currentUser.role === "administrator" || currentUser.role === "manager";

  const load = useCallback(async () => {
    setError("");
    const [settingsResponse, templateResponse, appSettingsResponse, diagnosticsResponse, tradeInPolicyResponse, modulesResponse, discountsResponse] = await Promise.all([
      api<InventorySettingsResponse>("/api/admin/inventory-settings"),
      api<{ templates: LabelTemplate[] }>("/api/admin/labels/templates"),
      api<{ settings: Record<string, unknown> }>("/api/admin/settings").catch((): { settings: Record<string, unknown> } => ({ settings: {} })),
      api<{ supplierSearch: SupplierSearchDiagnostics | null }>("/api/admin/diagnostics").catch((): { supplierSearch: SupplierSearchDiagnostics | null } => ({ supplierSearch: null })),
      api<{ policy: Partial<TradeInPolicySettings> & { basePercentage?: number } }>("/api/admin/trade-in-policy").catch(() => ({ policy: defaultTradeInPolicySettings })),
      api<{ modules: OrganizationModuleView[] }>("/api/admin/modules").catch((): { modules: OrganizationModuleView[] } => ({ modules: [] })),
      api<{ discounts: PosDiscountDefinition[] }>("/api/admin/pos/discounts").catch((): { discounts: PosDiscountDefinition[] } => ({ discounts: [] })),
    ]);
    const settingsData = normalizeSettingsData(settingsResponse, templateResponse.templates ?? [], shellStoreProfile);
    setData(settingsData);
    setAppSettings(appSettingsResponse.settings ?? {});
    setLabelStyle(normalizeLabelStyleSettings(appSettingsResponse.settings));
    const normalizedTradeInPolicy = normalizeTradeInPolicy(tradeInPolicyResponse.policy, settingsData.defaults.defaultCurrency);
    setTradeInPolicy(normalizedTradeInPolicy);
    setLoadedTradeInCreditMode(normalizedTradeInPolicy.creditMode);
    setTradeInConversionInput("");
    setSupplierProviders(normalizeSupplierProviders(appSettingsResponse.settings?.supplierSearchProviders));
    setSupplierDiagnostics(diagnosticsResponse.supplierSearch);
    setDiscounts(discountsResponse.discounts ?? []);
    const loadedModules = Array.isArray(modulesResponse.modules) ? modulesResponse.modules : [];
    if (loadedModules.length) {
      setModuleViews(loadedModules);
      onModulesChanged?.(loadedModules);
    }
    setShelfDraft((current) => ({ ...current, locationId: current.locationId || settingsData.defaults.defaultLocationId || settingsData.locations[0]?.id || "" }));
    if (canAdmin) {
      const profileResponse = await api<{ profile: PublicStoreProfile }>("/api/admin/store-profile");
      setStoreProfile({ ...defaultStoreProfile, ...profileResponse.profile });
    } else {
      setStoreProfile(null);
    }
  }, [api, canAdmin, shellStoreProfile, onModulesChanged]);

  useEffect(() => { void load().catch((err: unknown) => setError(settingsErrorMessage(err))); }, [load]);
  useEffect(() => { setThemeDraft(theme); }, [theme]);
  useEffect(() => { if (organizationModules.length) setModuleViews(organizationModules); }, [organizationModules]);

  const run = async (label: string, task: () => Promise<void>) => {
    setPending(label);
    setError("");
    setMessage("");
    try {
      await task();
      await load();
    } catch (err) {
      setError(settingsErrorMessage(err));
    } finally {
      setPending("");
    }
  };

  const saveDefaults = async () => {
    if (!data) return;
    await api<{ defaults: InventoryDefaults }>("/api/admin/inventory-settings", {
      method: "PUT",
      headers: { "idempotency-key": retainedKey("settings-defaults", "default", JSON.stringify(data.defaults)) },
      body: JSON.stringify(data.defaults),
    });
    setMessage("Inventory defaults saved from the server response.");
  };

  const saveStoreProfile = async () => {
    if (!storeProfile) return;
    const profilePayload = storeProfilePayload(storeProfile);
    await api<{ profile: PublicStoreProfile; changedFields: string[] }>("/api/admin/store-profile", {
      method: "PUT",
      headers: { "idempotency-key": retainedKey("settings-store-profile", "default", JSON.stringify(profilePayload)) },
      body: JSON.stringify(profilePayload),
    });
    setMessage("Store identity saved and reloaded.");
  };

  const uploadStoreLogo = async (file: File | null) => {
    if (!file || !storeProfile) return;
    await run("store-logo", async () => {
      const allowedTypes = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
      if (!allowedTypes.includes(file.type)) throw new Error("Upload a PNG, JPEG, WebP, or SVG logo.");
      if (file.size > 262_144) throw new Error("Logo image must be 256 KB or smaller.");
      const dataBase64 = await readFileBase64(file);
      const response = await api<{ profile: PublicStoreProfile }>("/api/admin/store-profile/logo", {
        method: "POST",
        headers: { "idempotency-key": retainedKey("settings-store-logo", file.name, `${file.name}:${file.size}:${file.lastModified}`) },
        body: JSON.stringify({ filename: file.name, contentType: file.type, dataBase64 }),
      });
      setStoreProfile({ ...defaultStoreProfile, ...response.profile });
      setMessage("Store logo uploaded and linked to labels.");
    });
  };

  const saveLocationDraft = async () => {
    await api<{ location: InventoryLocation }>("/api/admin/inventory-settings/locations", {
      method: "POST",
      headers: { "idempotency-key": retainedKey("settings-location", locationDraft.id || locationDraft.code, JSON.stringify(locationDraft)) },
      body: JSON.stringify(cleanLocationDraft(locationDraft)),
    });
    setLocationDraft(emptyLocationDraft);
    setMessage("Location saved and authoritative settings reloaded.");
  };

  const saveShelfDraft = async () => {
    await api<{ shelf: InventoryShelf }>("/api/admin/inventory-settings/shelves", {
      method: "POST",
      headers: { "idempotency-key": retainedKey("settings-shelf", shelfDraft.id || `${shelfDraft.locationId}:${shelfDraft.code}`, JSON.stringify(shelfDraft)) },
      body: JSON.stringify(cleanShelfDraft(shelfDraft)),
    });
    setShelfDraft({ ...emptyShelfDraft, locationId: shelfDraft.locationId });
    setMessage("Shelf saved and authoritative settings reloaded.");
  };

  const editLocationDraft = (location: InventoryLocation) => {
    setLocationDraft({ id: location.id, code: location.code, name: location.name, active: location.active });
    setMessage("Location loaded for editing.");
  };
  const editShelfDraft = (shelf: InventoryShelf) => {
    setShelfDraft({ id: shelf.id, locationId: shelf.locationId, code: shelf.code, name: shelf.name ?? "", description: "", active: shelf.active });
    setMessage("Shelf loaded for editing.");
  };
  const archiveLocationDraft = async (location: InventoryLocation) => {
    if (!window.confirm(`Archive ${location.name}? Locations with active inventory will be blocked.`)) return;
    await api<{ location: InventoryLocation }>(`/api/admin/inventory-settings/locations/${encodeURIComponent(location.id)}`, {
      method: "DELETE",
      headers: { "idempotency-key": retainedKey("settings-location-archive", location.id, String(location.active)) },
    });
    if (locationDraft.id === location.id) setLocationDraft(emptyLocationDraft);
    setMessage("Location archived and authoritative settings reloaded.");
  };
  const archiveShelfDraft = async (shelf: InventoryShelf) => {
    if (!window.confirm(`Archive ${shelf.code}? Shelves with active copies will be blocked.`)) return;
    await api<{ shelf: InventoryShelf }>(`/api/admin/inventory-settings/shelves/${encodeURIComponent(shelf.id)}`, {
      method: "DELETE",
      headers: { "idempotency-key": retainedKey("settings-shelf-archive", shelf.id, String(shelf.active)) },
    });
    if (shelfDraft.id === shelf.id) setShelfDraft({ ...emptyShelfDraft, locationId: shelf.locationId });
    setMessage("Shelf archived and authoritative settings reloaded.");
  };

  const saveFormatDraft = async () => {
    await api<{ format: CatalogFormat }>("/api/admin/inventory-settings/formats", {
      method: "POST",
      headers: { "idempotency-key": retainedKey("settings-format", formatDraft.code, JSON.stringify(formatDraft)) },
      body: JSON.stringify(cleanFormatDraft(formatDraft)),
    });
    setFormatDraft(emptyFormatDraft);
    setMessage("Format saved and authoritative settings reloaded.");
  };

  const saveConditionDraft = async () => {
    await api<{ condition: OperationalCondition }>("/api/admin/inventory-settings/conditions", {
      method: "POST",
      headers: { "idempotency-key": retainedKey("settings-condition", conditionDraft.code, JSON.stringify(conditionDraft)) },
      body: JSON.stringify(cleanConditionDraft(conditionDraft)),
    });
    setConditionDraft(emptyConditionDraft);
    setMessage("Condition saved and authoritative settings reloaded.");
  };
  const saveDiscountDraft = async () => {
    const payload = cleanDiscountDraft(discountDraft);
    await api<{ discount: PosDiscountDefinition }>("/api/admin/pos/discounts", {
      method: "POST",
      headers: { "idempotency-key": retainedKey("settings-pos-discount", payload.name, JSON.stringify(payload)) },
      body: JSON.stringify(payload),
    });
    setDiscountDraft(emptyDiscountDraft);
    setMessage("Discount rule saved and automatic register pricing will use it.");
  };
  const saveSupplierProviders = async () => {
    const body = { ...appSettings, supplierSearchProviders: supplierProviders };
    const response = await api<{ settings: Record<string, unknown> }>("/api/admin/settings", {
      method: "PUT",
      headers: { "idempotency-key": retainedKey("settings-supplier-search", "providers", JSON.stringify(supplierProviders)) },
      body: JSON.stringify(body),
    });
    const nextSettings = response.settings ?? body;
    setAppSettings(nextSettings);
    setSupplierProviders(normalizeSupplierProviders(nextSettings.supplierSearchProviders));
    setMessage("Supplier search settings saved.");
  };
  const saveLabelStyle = async () => {
    const body = { ...appSettings, ...labelStyle };
    const response = await api<{ settings: Record<string, unknown> }>("/api/admin/settings", {
      method: "PUT",
      headers: { "idempotency-key": retainedKey("settings-label-style", "default", JSON.stringify(labelStyle)) },
      body: JSON.stringify(body),
    });
    const nextSettings = response.settings ?? body;
    setAppSettings(nextSettings);
    setLabelStyle(normalizeLabelStyleSettings(nextSettings));
    setMessage("Label colors saved.");
  };
  const saveTradeInPolicy = async () => {
    if (!canManageTradeIns) throw new Error("Sign in as an administrator or manager to save trade-in settings.");
    const modeChanging = loadedTradeInCreditMode !== tradeInPolicy.creditMode;
    const conversionAmount = modeChanging ? inputToMinorUnits(tradeInConversionInput) : undefined;
    const payload = tradeInPolicyPayload(tradeInPolicy, conversionAmount);
    const response = await api<{ policy: Partial<TradeInPolicySettings> & { basePercentage?: number } }>("/api/admin/trade-in-policy", {
      method: "PUT",
      headers: { "idempotency-key": retainedKey("settings-trade-in-policy", "default", JSON.stringify(payload)) },
      body: JSON.stringify(payload),
    });
    const normalizedTradeInPolicy = normalizeTradeInPolicy(response.policy, data?.defaults.defaultCurrency ?? shellStoreProfile.currencyCode ?? "USD");
    setTradeInPolicy(normalizedTradeInPolicy);
    setLoadedTradeInCreditMode(normalizedTradeInPolicy.creditMode);
    setTradeInConversionInput("");
    setMessage("Trade-in policy saved.");
  };
  const updateSupplierProvider = (id: string, patch: Partial<SupplierSearchProvider>) => {
    setSupplierProviders((current) => current.map((provider) => provider.id === id ? { ...provider, ...patch } : provider));
  };
  const updateTradeInRule = (category: string, patch: Partial<TradeInRedemptionRule>) => {
    setTradeInPolicy((current) => {
      if (category === "default") return { ...current, redemptionRules: { ...current.redemptionRules, default: clampTradeInRule({ ...current.redemptionRules.default, ...patch }) } };
      const rule = current.redemptionRules.categories[category] ?? defaultTradeInPolicySettings.redemptionRules.categories[category] ?? defaultTradeInPolicySettings.redemptionRules.default;
      return {
        ...current,
        redemptionRules: {
          ...current.redemptionRules,
          categories: { ...current.redemptionRules.categories, [category]: clampTradeInRule({ ...rule, ...patch }) },
        },
      };
    });
  };

  const activeLocationName = data?.locations.find((location) => location.id === data.defaults.defaultLocationId)?.name ?? "Not configured";
  const labelTemplateName = data?.templates.find((template) => template.id === data.defaults.defaultLabelTemplateId)?.name ?? "No default label template";
  const tradeInModeChanging = loadedTradeInCreditMode !== tradeInPolicy.creditMode;
  const moduleEnabledMap = Object.fromEntries(moduleViews.map((entry) => [entry.state.moduleKey, entry.state.enabled])) as Partial<Record<ModuleKey, boolean>>;
  const settingsSections = data ? buildSettingsSections({ canAdmin, hasStoreProfile: Boolean(storeProfile), activeLocationName, labelTemplateName, tradeInPolicy, discounts, theme, supplierDiagnostics, supplierProviders, data, moduleEnabledMap }) : [];
  const managementAreas = buildSettingsAreaCards(currentUser.role).filter((area) => {
    if (area.path.includes("/loyalty")) return moduleEnabledMap.loyalty !== false;
    if (area.path.includes("/storefront")) return moduleEnabledMap.storefront !== false;
    return true;
  });
  const activeSection = settingsSections.find((section) => section.id === activeSettingsSection) ?? null;
  const canSaveActiveSettingsSection = !pending && (
    activeSettingsSection === "tradeIns" ? canManageTradeIns : canAdmin
  );
  const toggleModule = async (view: OrganizationModuleView) => {
    if (!canAdmin) {
      setError("Administrator authority is required to change organization modules.");
      return;
    }
    const nextEnabled = !view.state.enabled;
    if (nextEnabled && view.definition.availability === "launch_deferred") {
      setError(`${view.definition.displayName} is deferred for the initial PaperbackOS launch and cannot be enabled yet.`);
      return;
    }
    if (view.definition.classification === "core" && !nextEnabled) {
      setError(`${view.definition.displayName} is a core module and cannot be disabled.`);
      return;
    }
    const disabledDependencies = view.definition.dependencies.filter((key) => moduleEnabledMap[key] === false);
    const enabledDependents = view.dependents.filter((key) => moduleEnabledMap[key] !== false);
    if (nextEnabled && disabledDependencies.length) {
      const names = disabledDependencies.map((key) => getModuleDefinition(key).displayName).join(", ");
      if (!window.confirm(`Enable ${names} with ${view.definition.displayName}? Dependencies must be enabled together.`)) return;
    }
    if (!nextEnabled && enabledDependents.length) {
      setError(`${view.definition.displayName} is required by ${enabledDependents.map((key) => getModuleDefinition(key).displayName).join(", ")}. Disable dependent modules first.`);
      return;
    }
    const consequence = nextEnabled
      ? `Enable ${view.definition.displayName} for this organization? Existing data becomes accessible again.`
      : `Disable ${view.definition.displayName}? Navigation, new activity, API mutations, and related background work will stop where applicable. Existing data will be preserved.`;
    if (!window.confirm(consequence)) return;
    setModulePendingKey(view.definition.key);
    setError("");
    try {
      const response = await api<{ modules: OrganizationModuleView[] }>(`/api/admin/modules/${encodeURIComponent(view.definition.key)}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: nextEnabled, reason: moduleReason || undefined, enableDependencies: nextEnabled && disabledDependencies.length > 0 }),
      });
      setModuleViews(response.modules);
      onModulesChanged?.(response.modules);
      setMessage(`${view.definition.displayName} ${nextEnabled ? "enabled" : "disabled"}.`);
      setModuleReason("");
    } catch (err) {
      setError(settingsErrorMessage(err));
    } finally {
      setModulePendingKey("");
    }
  };
  const saveActiveSettingsSection = async () => {
    if (!activeSettingsSection) return;
    if (activeSettingsSection === "appearance") {
      onThemeChange(themeDraft);
      setMessage("Appearance saved.");
    }
    if (activeSettingsSection === "operations") await saveDefaults();
    if (activeSettingsSection === "discounts") await saveDiscountDraft();
    if (activeSettingsSection === "tradeIns") await saveTradeInPolicy();
    if (activeSettingsSection === "profile") await saveStoreProfile();
    if (activeSettingsSection === "suppliers") await saveSupplierProviders();
    if (activeSettingsSection === "labels") await saveLabelStyle();
    if (activeSettingsSection === "modules") setMessage("Module changes are saved from each module row.");
    if (activeSettingsSection === "structure") {
      if (locationDraft.code && locationDraft.name) await saveLocationDraft();
      else if (shelfDraft.locationId && shelfDraft.code) await saveShelfDraft();
    }
    if (activeSettingsSection === "catalog") {
      if (formatDraft.code && formatDraft.displayName) await saveFormatDraft();
      else if (conditionDraft.code && conditionDraft.displayName) await saveConditionDraft();
    }
    setActiveSettingsSection(null);
  };
  const cancelActiveSettingsSection = () => {
    setThemeDraft(theme);
    setActiveSettingsSection(null);
    void load().catch((err: unknown) => setError(settingsErrorMessage(err)));
  };

  return (
    <section className="page settings-workspace">
      <header className="page-header">
        <div>
          <p className="eyebrow">Operations setup</p>
          <h1>Settings</h1>
        </div>
        <button onClick={() => void run("refresh", load)} disabled={Boolean(pending)}><RefreshCw /> Refresh</button>
      </header>
      {message && <div className="success" role="status">{message}</div>}
      {error && <div className="error" role="alert"><AlertTriangle /> {error}</div>}
      {!canAdmin && (
        <div className="feature-panel" role="status">
          <AlertTriangle /> Administrator authority is required to change store profile, structural inventory settings, label templates, locations, shelves, formats and conditions. The backend remains authoritative for every save.
        </div>
      )}
      {!data ? <p className="empty-inline">Loading authoritative settings...</p> : (
        <>
          <section className="settings-summary-grid" aria-label="Current settings summary">
            <article><span>Default location</span><strong>{activeLocationName}</strong></article>
            <article><span>Default tracking</span><strong>{data.defaults.defaultTrackingMode}</strong></article>
            <article><span>Currency</span><strong>{data.defaults.defaultCurrency}</strong></article>
            <article><span>Label template</span><strong>{labelTemplateName}</strong></article>
          </section>
          {managementAreas.length > 0 && (
            <section aria-label="Business settings">
              <div className="section-title">
                <div><p className="eyebrow">Business settings</p><h2>Loyalty, storefront, and onboarding</h2></div>
              </div>
              <div className="settings-section-grid">
                {managementAreas.map((area) => (
                  <button
                    key={area.path}
                    className="settings-section-tile"
                    aria-label={`Open ${area.title}`}
                    onClick={() => onNavigate ? onNavigate(area.path) : window.location.assign(area.path)}
                  >
                    <area.icon aria-hidden />
                    <strong>{area.title}</strong>
                    <span>{area.summary}</span>
                  </button>
                ))}
              </div>
            </section>
          )}
          <div className="section-title">
            <div><p className="eyebrow">Store operations</p><h2>Inventory and store setup</h2></div>
          </div>
          <section className="settings-section-grid" aria-label="Settings sections">
            {settingsSections.map((section) => (
              <button
                key={section.id}
                className="settings-section-tile"
                aria-label={`Open ${section.title} settings`}
                onClick={() => setActiveSettingsSection(section.id)}
                disabled={section.disabled}
              >
                <section.icon aria-hidden />
                <strong>{section.title}</strong>
                <span>{section.summary}</span>
              </button>
            ))}
          </section>
          {activeSection && (
            <div className="modal-backdrop" role="presentation">
              <section className="modal settings-section-dialog" role="dialog" aria-modal="true" aria-labelledby="settings-section-title">
                <div className="section-title">
                  <div><p className="eyebrow">{activeSection.eyebrow}</p><h2 id="settings-section-title">{activeSection.title}</h2></div>
                  <button className="icon" aria-label="Close settings section" onClick={cancelActiveSettingsSection}><X /></button>
                </div>
                {error && <div className="error" role="alert"><AlertTriangle /> {error}</div>}
                {message && <div className="success" role="status">{message}</div>}
                <div className="settings-layout settings-section-body">
            <section className="panel" hidden={activeSettingsSection !== "appearance"}>
              <div className="section-title">
                <div><p className="eyebrow">Appearance</p><h2>Theme</h2></div>
              </div>
              <div className="form-grid compact">
                <label>Theme
                  <select value={themeDraft} disabled={!canAdmin} onChange={(event) => setThemeDraft(event.target.value as ThemeMode)}>
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                </label>
              </div>
            </section>

            <section className="panel" hidden={activeSettingsSection !== "operations"}>
              <div className="section-title">
                <div><p className="eyebrow">Operations</p><h2>Receiving defaults</h2></div>
              </div>
              <div className="form-grid compact">
                <label>Default location<select value={data.defaults.defaultLocationId} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, defaultLocationId: event.target.value } })}>{data.locations.map((location) => <option key={location.id} value={location.id}>{location.name} ({location.code})</option>)}</select></label>
                <label>Default shelf<select value={data.defaults.defaultShelfId ?? ""} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, defaultShelfId: event.target.value || undefined } })}><option value="">No default shelf</option>{data.shelves.filter((shelf) => !data.defaults.defaultLocationId || shelf.locationId === data.defaults.defaultLocationId).map((shelf) => <option key={shelf.id} value={shelf.id}>{shelf.code}{shelf.name ? ` - ${shelf.name}` : ""}</option>)}</select></label>
                <label>Default format<select value={data.defaults.defaultFormatId ?? ""} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, defaultFormatId: event.target.value || undefined } })}><option value="">No default format</option>{data.formats.map((format) => <option key={format.id} value={format.id}>{format.displayName}</option>)}</select></label>
                <label>Default condition<select value={data.defaults.defaultConditionId ?? ""} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, defaultConditionId: event.target.value || undefined } })}><option value="">No default condition</option>{data.conditions.map((condition) => <option key={condition.id} value={condition.id}>{condition.displayName}</option>)}</select></label>
                <label>Tracking default<select value={data.defaults.defaultTrackingMode} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, defaultTrackingMode: event.target.value as "copy" | "quantity" } })}><option value="quantity">Quantity</option><option value="copy">Exact copies</option></select></label>
                <label>Currency<input value={data.defaults.defaultCurrency} disabled={!canAdmin} maxLength={3} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, defaultCurrency: event.target.value.toUpperCase() } })} /></label>
                <label>Barcode prefix<input value={data.defaults.barcodePrefix} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, barcodePrefix: event.target.value.toUpperCase() } })} /></label>
                <label>Barcode minimum width<input type="number" min={1} max={32} value={data.defaults.barcodeMinWidth} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, barcodeMinWidth: Number(event.target.value) } })} /></label>
                <label>Labels per copy<input type="number" min={0} max={20} value={data.defaults.labelsPerCopy} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, labelsPerCopy: Number(event.target.value) } })} /></label>
                <label>Metadata behavior<select value={data.defaults.receivingMetadataBehavior} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, receivingMetadataBehavior: event.target.value as InventoryDefaults["receivingMetadataBehavior"] } })}><option value="disabled">Disabled</option><option value="suggest">Suggest</option><option value="fill_blank">Fill blanks</option></select></label>
                <label>Duplicate scan behavior<select value={data.defaults.duplicateScanBehavior} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, duplicateScanBehavior: event.target.value as InventoryDefaults["duplicateScanBehavior"] } })}><option value="increment_quantity">Increment quantity</option><option value="warn">Warn</option></select></label>
                <label>Default label template<select value={data.defaults.defaultLabelTemplateId ?? ""} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, defaultLabelTemplateId: event.target.value || undefined } })}><option value="">No default template</option>{data.templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}</select></label>
                <label className="checkbox-row"><input type="checkbox" checked={data.defaults.requirePriceOnReceive} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, requirePriceOnReceive: event.target.checked } })} /> Price required before receiving</label>
                <label className="checkbox-row"><input type="checkbox" checked={data.defaults.requireCostOnReceive} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, requireCostOnReceive: event.target.checked } })} /> Cost required before receiving</label>
                <label className="checkbox-row"><input type="checkbox" checked={data.defaults.autoGenerateBarcodes} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, autoGenerateBarcodes: event.target.checked } })} /> Auto-generate store barcodes</label>
                <label className="checkbox-row"><input type="checkbox" checked={data.defaults.offerLabelBatchAfterReceiving} disabled={!canAdmin} onChange={(event) => setData({ ...data, defaults: { ...data.defaults, offerLabelBatchAfterReceiving: event.target.checked } })} /> Offer label batch after receiving</label>
              </div>
              <p className="empty-inline">These defaults are saved through `/api/admin/inventory-settings`. The current backend does not expose a version field, so the UI reloads authoritative settings after every save and does not silently overwrite failed validation.</p>
            </section>

            <section className="panel" hidden={activeSettingsSection !== "discounts"}>
              <div className="section-title">
                <div><p className="eyebrow">Pricing</p><h2>Discount rules</h2></div>
              </div>
              <div className="settings-list" aria-label="Discount rules">
                {discounts.length === 0 && <p className="empty-inline">No discount rules are configured.</p>}
                {discounts.map((discount) => (
                  <article key={discount.id}>
                    <strong>{discount.name}</strong>
                    <span>
                      {discount.active ? "Active" : "Inactive"} / {discount.applicationMode} / {discount.scope} / {formatDiscountValue(discount, data.defaults.defaultCurrency)}
                    </span>
                    <p>{discountRuleSummary(discount, data.defaults.defaultCurrency)}</p>
                    <button disabled={!canAdmin} onClick={() => setDiscountDraft(discountToDraft(discount))}>Edit</button>
                  </article>
                ))}
              </div>
              <div className="form-grid compact">
                <label>Name<input value={discountDraft.name} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, name: event.target.value })} /></label>
                <label>Discount type<select value={discountDraft.discountType} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, discountType: event.target.value as PosDiscountDefinition["discountType"] })}><option value="percentage">Percentage</option><option value="fixed">Dollar amount</option></select></label>
                <label>{discountDraft.discountType === "percentage" ? "Percent off" : "Amount off"}<input inputMode="decimal" value={discountDraft.value} disabled={!canAdmin} placeholder={discountDraft.discountType === "percentage" ? "10" : "5.00"} onChange={(event) => setDiscountDraft({ ...discountDraft, value: event.target.value })} /></label>
                <label>Applies to<select value={discountDraft.scope} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, scope: event.target.value as PosDiscountDefinition["scope"] })}><option value="order">Whole sale</option><option value="line">Matching items</option></select></label>
                <label>Mode<select value={discountDraft.applicationMode} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, applicationMode: event.target.value as PosDiscountDefinition["applicationMode"] })}><option value="automatic">Automatic</option><option value="manual">Manual</option><option value="both">Manual and automatic</option></select></label>
                <label>Priority<input type="number" min={0} max={9999} value={discountDraft.priority} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, priority: nonnegativeInt(Number(event.target.value)) })} /></label>
                <label>Minimum sale<input inputMode="decimal" value={discountDraft.minimumSubtotal} disabled={!canAdmin} placeholder="0.00" onChange={(event) => setDiscountDraft({ ...discountDraft, minimumSubtotal: event.target.value })} /></label>
                <label>Minimum items<input type="number" min={0} value={discountDraft.minimumItemCount} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, minimumItemCount: event.target.value })} /></label>
                <label>Minimum matching items<input type="number" min={0} value={discountDraft.minimumMatchingItemCount} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, minimumMatchingItemCount: event.target.value })} /></label>
                <label>Product type<select value={discountDraft.productType} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, productType: event.target.value as "" | ProductType })}><option value="">Any product</option>{productTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                <label>Format contains<input value={discountDraft.formatMatch} disabled={!canAdmin} placeholder="paperback, dvd, vinyl" onChange={(event) => setDiscountDraft({ ...discountDraft, formatMatch: event.target.value })} /></label>
                <label>Condition contains<input value={discountDraft.conditionMatch} disabled={!canAdmin} placeholder="used, new, good" onChange={(event) => setDiscountDraft({ ...discountDraft, conditionMatch: event.target.value })} /></label>
                <label>Title contains<input value={discountDraft.titleMatch} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, titleMatch: event.target.value })} /></label>
                <label>Starts<input type="date" value={discountDraft.startsAt} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, startsAt: event.target.value })} /></label>
                <label>Ends<input type="date" value={discountDraft.endsAt} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, endsAt: event.target.value })} /></label>
                <label>Start time<input type="time" value={discountDraft.startTime} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, startTime: event.target.value })} /></label>
                <label>End time<input type="time" value={discountDraft.endTime} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, endTime: event.target.value })} /></label>
                <div className="wide checkbox-cluster" aria-label="Discount days">
                  {daysOfWeekOptions.map((day) => (
                    <label className="checkbox-row" key={day.value}>
                      <input
                        type="checkbox"
                        checked={discountDraft.daysOfWeek.includes(day.value)}
                        disabled={!canAdmin}
                        onChange={(event) => setDiscountDraft({ ...discountDraft, daysOfWeek: toggleString(discountDraft.daysOfWeek, day.value, event.target.checked) })}
                      />
                      {day.label}
                    </label>
                  ))}
                </div>
                <label className="checkbox-row"><input type="checkbox" checked={discountDraft.active} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, active: event.target.checked })} /> Active</label>
                <label className="checkbox-row"><input type="checkbox" checked={discountDraft.stackable} disabled={!canAdmin} onChange={(event) => setDiscountDraft({ ...discountDraft, stackable: event.target.checked })} /> Can stack with other automatic discounts</label>
              </div>
              <div className="actions">
                <button disabled={!canAdmin} onClick={() => setDiscountDraft(emptyDiscountDraft)}>New rule</button>
              </div>
              <p className="empty-inline">Automatic discounts apply on draft register sales as items are added or removed. Manual discount selection can be added to the register flow next.</p>
            </section>

            <section className="panel" hidden={activeSettingsSection !== "tradeIns"}>
              <div className="section-title">
                <div><p className="eyebrow">Trade-ins</p><h2>Credit policy</h2></div>
              </div>
              {!canManageTradeIns && <div className="feature-panel" role="status"><AlertTriangle /> Sign in as an administrator or manager to save trade-in settings.</div>}
              <div className="form-grid compact">
                <label className="checkbox-row"><input type="checkbox" checked={tradeInPolicy.enabled} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, enabled: event.target.checked })} /> Trade-ins enabled</label>
                <label>Credit mode<select value={tradeInPolicy.creditMode} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, creditMode: event.target.value as TradeInCreditMode })}><option value="dollar_amount">Dollar amount</option><option value="one_for_one">1-to-1 credit</option></select></label>
                <label>Dollar amount percentage<input type="number" min={0} max={100} value={tradeInPolicy.defaultCreditPercentage} disabled={!canManageTradeIns || tradeInPolicy.creditMode !== "dollar_amount"} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, defaultCreditPercentage: clampPercent(Number(event.target.value)) })} /></label>
                {tradeInPolicy.creditMode === "one_for_one" && <p className="empty-inline wide">One accepted book creates one trade credit.</p>}
                {tradeInModeChanging && <label className="wide">Dollar value per trade credit<input inputMode="decimal" value={tradeInConversionInput} disabled={!canManageTradeIns} placeholder="0.00" onChange={(event) => setTradeInConversionInput(event.target.value)} /></label>}
                {tradeInModeChanging && <p className="empty-inline wide">Existing balances will be converted using this value when the mode change is saved.</p>}
                <label>Minimum item price<input inputMode="decimal" value={minorUnitsToInput(tradeInPolicy.minItemPriceAmount)} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, minItemPriceAmount: inputToMinorUnits(event.target.value) })} /></label>
                <label>Maximum item price<input inputMode="decimal" value={minorUnitsToInput(tradeInPolicy.maxItemPriceAmount)} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, maxItemPriceAmount: inputToMinorUnits(event.target.value) })} /></label>
                <label>Currency<input maxLength={3} value={tradeInPolicy.currency} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, currency: event.target.value.toUpperCase() })} /></label>
                <label>Quote expiration minutes<input type="number" min={0} value={tradeInPolicy.quoteExpirationMinutes} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, quoteExpirationMinutes: Math.max(0, Math.floor(Number(event.target.value) || 0)) })} /></label>
                <label>Manager approval threshold<input inputMode="decimal" value={minorUnitsToInput(tradeInPolicy.managerApprovalThresholdAmount)} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, managerApprovalThresholdAmount: inputToMinorUnits(event.target.value) })} /></label>
                <label className="checkbox-row"><input type="checkbox" checked={tradeInPolicy.requireCustomer} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, requireCustomer: event.target.checked })} /> Customer required</label>
                <label className="checkbox-row"><input type="checkbox" checked={tradeInPolicy.requireInspection} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, requireInspection: event.target.checked })} /> Inspection required</label>
                <label className="wide">Policy text<textarea value={tradeInPolicy.policyText ?? ""} disabled={!canManageTradeIns} onChange={(event) => setTradeInPolicy({ ...tradeInPolicy, policyText: emptyToNull(event.target.value) })} /></label>
              </div>
              <div className="settings-list" aria-label="Trade-in credit redemption limits">
                {tradeInRedemptionCategories.map((category) => {
                  const rule = category.key === "default" ? tradeInPolicy.redemptionRules.default : tradeInPolicy.redemptionRules.categories[category.key] ?? defaultTradeInPolicySettings.redemptionRules.default;
                  return (
                    <article key={category.key}>
                      <strong>{category.label}</strong>
                      <span>{rule.enabled ? `${rule.maxPercent}% of each matching book can use store credit` : "Store credit disabled"}</span>
                      <div className="form-grid compact">
                        <label className="checkbox-row"><input type="checkbox" checked={rule.enabled} disabled={!canManageTradeIns} onChange={(event) => updateTradeInRule(category.key, { enabled: event.target.checked })} /> Allow credit</label>
                        <label>Maximum credit percent<input type="number" min={0} max={100} value={rule.maxPercent} disabled={!canManageTradeIns || !rule.enabled} onChange={(event) => updateTradeInRule(category.key, { maxPercent: clampPercent(Number(event.target.value)) })} /></label>
                      </div>
                    </article>
                  );
                })}
              </div>
              <p className="empty-inline">Category limits apply when store credit is held on a POS sale. A disabled category contributes no store-credit allowance for matching sale lines.</p>
            </section>

            {canAdmin && storeProfile && (
              <section className="panel" hidden={activeSettingsSection !== "profile"}>
                <div className="section-title">
                  <div><p className="eyebrow">Store identity</p><h2>Profile</h2></div>
                </div>
                <div className="form-grid compact">
                  <label>Product<input value={storeProfile.productName} readOnly /></label>
                  <label>Store display name<input value={storeProfile.storeDisplayName} onChange={(event) => setStoreProfile({ ...storeProfile, storeDisplayName: event.target.value })} /></label>
                  <label>Store tagline<input value={storeProfile.storeTagline ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, storeTagline: emptyToNull(event.target.value) })} /></label>
                  <label>Logo URL<input value={storeProfile.logoUrl ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, logoUrl: emptyToNull(event.target.value) })} /></label>
                  <label>Upload logo<input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" disabled={pending === "store-logo"} onChange={(event) => void uploadStoreLogo(event.target.files?.[0] ?? null)} /></label>
                  {storeProfile.logoUrl && <div className="logo-preview" aria-label="Current store logo"><img src={storeProfile.logoUrl} alt="Store logo preview" /></div>}
                  <label>Public website<input value={storeProfile.publicWebsiteUrl ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, publicWebsiteUrl: emptyToNull(event.target.value) })} /></label>
                  <label>Public email<input type="email" value={storeProfile.publicEmail ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, publicEmail: emptyToNull(event.target.value) })} /></label>
                  <label>Public phone<input value={storeProfile.publicPhone ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, publicPhone: emptyToNull(event.target.value) })} /></label>
                  <label>Timezone<input value={storeProfile.timezone ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, timezone: emptyToNull(event.target.value) })} /></label>
                  <label>Currency code<input maxLength={3} value={storeProfile.currencyCode ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, currencyCode: emptyToNull(event.target.value.toUpperCase()) })} /></label>
                  <label>Storefront title<input value={storeProfile.storefrontTitle ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, storefrontTitle: emptyToNull(event.target.value) })} /></label>
                  <label className="wide">Storefront description<textarea value={storeProfile.storefrontDescription ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, storefrontDescription: emptyToNull(event.target.value) })} /></label>
                  <label className="wide">Store description<textarea value={storeProfile.storeDescription ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, storeDescription: emptyToNull(event.target.value) })} /></label>
                  <label>Address line 1<input value={storeProfile.addressLine1 ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, addressLine1: emptyToNull(event.target.value) })} /></label>
                  <label>Address line 2<input value={storeProfile.addressLine2 ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, addressLine2: emptyToNull(event.target.value) })} /></label>
                  <label>City<input value={storeProfile.city ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, city: emptyToNull(event.target.value) })} /></label>
                  <label>State / region<input value={storeProfile.region ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, region: emptyToNull(event.target.value) })} /></label>
                  <label>Postal code<input value={storeProfile.postalCode ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, postalCode: emptyToNull(event.target.value) })} /></label>
                  <label>Country<input value={storeProfile.country ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, country: emptyToNull(event.target.value) })} /></label>
                  <label className="wide">Receipt footer text<textarea value={storeProfile.receiptFooterText ?? ""} onChange={(event) => setStoreProfile({ ...storeProfile, receiptFooterText: emptyToNull(event.target.value) })} /></label>
                </div>
                <p className="empty-inline">Store identity excludes secrets, Square credentials, webhook keys, D1 bindings, deployment URLs, taxes and feature flags.</p>
              </section>
            )}

            <section className="panel" hidden={activeSettingsSection !== "suppliers"}>
              <div className="section-title">
                <div><p className="eyebrow">Sourcing</p><h2>Supplier searches</h2></div>
              </div>
              <p className="empty-inline">Google Shopping uses the server-side SerpApi key when configured. Link providers use HTTPS templates with {"{query}"}, {"{isbn}"}, {"{title}"}, or {"{author}"} placeholders.</p>
              <div className="settings-summary-grid" aria-label="Supplier search diagnostics">
                <div><span>SerpApi key</span><strong>{supplierDiagnostics?.serpApiConfigured ? "Configured" : "Not detected"}</strong></div>
                <div><span>Google Shopping provider</span><strong>{supplierProviders.find((provider) => provider.id === "google_shopping")?.enabled ? "Enabled" : "Disabled"}</strong></div>
                <div><span>API mode</span><strong>{supplierProviders.find((provider) => provider.id === "google_shopping")?.mode === "serpapi_google_shopping" ? "Google Shopping" : "Link only"}</strong></div>
              </div>
              <div className="supplier-settings">
                {supplierProviders.map((provider, index) => (
                  <article className="supplier-setting-row" key={provider.id}>
                    <label className="checkbox-row"><input type="checkbox" checked={provider.enabled} disabled={!canAdmin} onChange={(event) => updateSupplierProvider(provider.id, { enabled: event.target.checked })} /> Enabled</label>
                    <label>Name<input value={provider.name} disabled={!canAdmin} onChange={(event) => updateSupplierProvider(provider.id, { name: event.target.value })} /></label>
                    <label>Mode<select value={provider.mode} disabled={!canAdmin} onChange={(event) => updateSupplierProvider(provider.id, { mode: event.target.value as SupplierSearchProvider["mode"] })}><option value="link">Search link</option><option value="serpapi_google_shopping">Google Shopping</option><option value="ebay_api">eBay API</option><option value="google_books">Google Books</option><option value="wholesale_fixture">Wholesale fixture</option></select></label>
                    <label className="wide">URL template<input value={provider.urlTemplate} disabled={!canAdmin} onChange={(event) => updateSupplierProvider(provider.id, { urlTemplate: event.target.value })} /></label>
                    <div className="actions wide">
                      <button disabled={!canAdmin || index === 0} onClick={() => setSupplierProviders(moveSupplierProvider(supplierProviders, index, index - 1))}>Move up</button>
                      <button disabled={!canAdmin || index === supplierProviders.length - 1} onClick={() => setSupplierProviders(moveSupplierProvider(supplierProviders, index, index + 1))}>Move down</button>
                      <button disabled={!canAdmin || provider.id === "google_shopping"} onClick={() => setSupplierProviders(supplierProviders.filter((entry) => entry.id !== provider.id))}>Remove</button>
                    </div>
                  </article>
                ))}
              </div>
              <div className="actions">
                <button disabled={!canAdmin} onClick={() => setSupplierProviders([...supplierProviders, { id: `supplier_${Date.now()}`, name: "New supplier", enabled: true, mode: "link", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: [], urlTemplate: "https://example.com/search?q={query}" }])}>Add supplier</button>
                <button disabled={!canAdmin} onClick={() => setSupplierProviders(normalizeSupplierProviders())}>Reset supplier defaults</button>
              </div>
              <p className="empty-inline">Secret values are never displayed here. If SerpApi is not detected after setting the key, set `SERPAPI_API_KEY` on the sandbox Pages project and redeploy.</p>
            </section>

            <section className="panel" hidden={activeSettingsSection !== "structure"}>
              <div className="section-title"><div><p className="eyebrow">Structure</p><h2>Locations and shelves</h2></div></div>
              <div className="settings-list">
                {data.locations.map((location) => (
                  <article key={location.id} className="settings-row-with-actions">
                    <div>
                      <strong>{location.name}</strong>
                      <span>{location.code} / {location.active ? "active" : "inactive"}</span>
                    </div>
                    <div className="row-actions">
                      <button type="button" className="icon" disabled={!canAdmin} aria-label={`Edit ${location.name}`} title="Edit location" onClick={() => editLocationDraft(location)}><Edit3 /></button>
                      <button type="button" className="icon danger" disabled={!canAdmin || !location.active} aria-label={`Archive ${location.name}`} title="Archive location" onClick={() => void run("archive-location", () => archiveLocationDraft(location))}><Trash2 /></button>
                    </div>
                  </article>
                ))}
              </div>
              <div className="form-grid compact">
                <label>Location ID<input value={locationDraft.id ?? ""} disabled={!canAdmin} placeholder="Leave blank for code-based ID" onChange={(event) => setLocationDraft({ ...locationDraft, id: event.target.value || undefined })} /></label>
                <label>Location code<input value={locationDraft.code} disabled={!canAdmin} onChange={(event) => setLocationDraft({ ...locationDraft, code: normalizeCode(event.target.value) })} /></label>
                <label>Location name<input value={locationDraft.name} disabled={!canAdmin} onChange={(event) => setLocationDraft({ ...locationDraft, name: event.target.value })} /></label>
                <label className="checkbox-row"><input type="checkbox" checked={locationDraft.active} disabled={!canAdmin} onChange={(event) => setLocationDraft({ ...locationDraft, active: event.target.checked })} /> Active location</label>
                {locationDraft.id && <button type="button" disabled={!canAdmin} onClick={() => setLocationDraft(emptyLocationDraft)}>Clear location edit</button>}
              </div>
              <div className="settings-list">
                {data.shelves.map((shelf) => (
                  <article key={shelf.id} className="settings-row-with-actions">
                    <div>
                      <strong>{shelf.code}{shelf.name ? ` - ${shelf.name}` : ""}</strong>
                      <span>{data.locations.find((location) => location.id === shelf.locationId)?.name ?? shelf.locationId} / {shelf.active ? "active" : "inactive"}</span>
                    </div>
                    <div className="row-actions">
                      <button type="button" className="icon" disabled={!canAdmin} aria-label={`Edit ${shelf.code}`} title="Edit shelf" onClick={() => editShelfDraft(shelf)}><Edit3 /></button>
                      <button type="button" className="icon danger" disabled={!canAdmin || !shelf.active} aria-label={`Archive ${shelf.code}`} title="Archive shelf" onClick={() => void run("archive-shelf", () => archiveShelfDraft(shelf))}><Trash2 /></button>
                    </div>
                  </article>
                ))}
              </div>
              <div className="form-grid compact">
                <label>Shelf location<select value={shelfDraft.locationId} disabled={!canAdmin} onChange={(event) => setShelfDraft({ ...shelfDraft, locationId: event.target.value })}>{data.locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label>
                <label>Shelf code<input value={shelfDraft.code} disabled={!canAdmin} onChange={(event) => setShelfDraft({ ...shelfDraft, code: normalizeCode(event.target.value) })} /></label>
                <label>Shelf name<input value={shelfDraft.name ?? ""} disabled={!canAdmin} onChange={(event) => setShelfDraft({ ...shelfDraft, name: event.target.value })} /></label>
                <label className="wide">Description<input value={shelfDraft.description ?? ""} disabled={!canAdmin} onChange={(event) => setShelfDraft({ ...shelfDraft, description: event.target.value })} /></label>
                <label className="checkbox-row"><input type="checkbox" checked={shelfDraft.active} disabled={!canAdmin} onChange={(event) => setShelfDraft({ ...shelfDraft, active: event.target.checked })} /> Active shelf</label>
                {shelfDraft.id && <button type="button" disabled={!canAdmin} onClick={() => setShelfDraft({ ...emptyShelfDraft, locationId: shelfDraft.locationId })}>Clear shelf edit</button>}
              </div>
              <p className="empty-inline">Unsafe deactivation is blocked by the backend when active stock or copies still reference a location or shelf.</p>
            </section>

            <section className="panel" hidden={activeSettingsSection !== "catalog"}>
              <div className="section-title"><div><p className="eyebrow">Catalog setup</p><h2>Formats and conditions</h2></div></div>
              <div className="settings-list">
                {data.formats.map((format) => <article key={format.id}><strong>{format.displayName}</strong><span>{format.code} / order {format.displayOrder} / {format.active ? "active" : "inactive"}</span></article>)}
              </div>
              <div className="form-grid compact">
                <label>Format code<input value={formatDraft.code} disabled={!canAdmin} onChange={(event) => setFormatDraft({ ...formatDraft, code: normalizeCode(event.target.value) })} /></label>
                <label>Format display name<input value={formatDraft.displayName} disabled={!canAdmin} onChange={(event) => setFormatDraft({ ...formatDraft, displayName: event.target.value })} /></label>
                <label>Display order<input type="number" value={formatDraft.displayOrder} disabled={!canAdmin} onChange={(event) => setFormatDraft({ ...formatDraft, displayOrder: Number(event.target.value) })} /></label>
                <label className="checkbox-row"><input type="checkbox" checked={formatDraft.active} disabled={!canAdmin} onChange={(event) => setFormatDraft({ ...formatDraft, active: event.target.checked })} /> Active format</label>
              </div>
              <div className="settings-list">
                {data.conditions.map((condition) => <article key={condition.id}><strong>{condition.displayName}</strong><span>{condition.code} / order {condition.sortOrder} / {condition.active ? "active" : "inactive"}</span>{condition.description && <p>{condition.description}</p>}</article>)}
              </div>
              <div className="form-grid compact">
                <label>Condition code<input value={conditionDraft.code} disabled={!canAdmin} onChange={(event) => setConditionDraft({ ...conditionDraft, code: normalizeCode(event.target.value) })} /></label>
                <label>Condition display name<input value={conditionDraft.displayName} disabled={!canAdmin} onChange={(event) => setConditionDraft({ ...conditionDraft, displayName: event.target.value })} /></label>
                <label>Sort order<input type="number" value={conditionDraft.sortOrder} disabled={!canAdmin} onChange={(event) => setConditionDraft({ ...conditionDraft, sortOrder: Number(event.target.value) })} /></label>
                <label className="wide">Description<input value={conditionDraft.description ?? ""} disabled={!canAdmin} onChange={(event) => setConditionDraft({ ...conditionDraft, description: event.target.value })} /></label>
                <label className="checkbox-row"><input type="checkbox" checked={conditionDraft.active} disabled={!canAdmin} onChange={(event) => setConditionDraft({ ...conditionDraft, active: event.target.checked })} /> Active condition</label>
              </div>
            </section>

            <section className="panel" hidden={activeSettingsSection !== "labels"}>
              <div className="section-title"><div><p className="eyebrow">Labels</p><h2>Avery sheet profiles</h2></div></div>
              <div className="form-grid compact">
                <label>Label background color<input type="color" value={labelStyle.labelBackgroundColor} disabled={!canAdmin} onChange={(event) => setLabelStyle({ ...labelStyle, labelBackgroundColor: event.target.value.toUpperCase() })} /></label>
                <label>Label text color<input type="color" value={labelStyle.labelTextColor} disabled={!canAdmin} onChange={(event) => setLabelStyle({ ...labelStyle, labelTextColor: event.target.value.toUpperCase() })} /></label>
                <div className="label-color-preview" style={{ backgroundColor: labelStyle.labelBackgroundColor, color: labelStyle.labelTextColor }}>
                  <strong>The Dreadful Bookshop</strong>
                  <span>Sample title</span>
                  <b>$16.99</b>
                </div>
              </div>
              <div className="settings-list">
                {data.templates.map((template) => (
                  <article key={template.id}>
                    <strong>{template.name}</strong>
                    <span>{formatMm(template.labelWidthMm)} x {formatMm(template.labelHeightMm)} mm / {template.rows} rows x {template.columns} columns / {template.rows * template.columns} labels per sheet</span>
                    {template.description && <p>{template.description}</p>}
                  </article>
                ))}
                {data.templates.length === 0 && <p className="empty-inline">No Avery label profiles are available.</p>}
              </div>
              <p className="empty-inline">Label profiles are fixed Avery US Letter sheets. Pick the default profile in Receiving defaults; custom sheet creation is intentionally unavailable so printed labels line up with known stock.</p>
            </section>

            <section className="panel" hidden={activeSettingsSection !== "modules"}>
              <div className="section-title">
                <div><p className="eyebrow">Organization modules</p><h2>Features</h2></div>
              </div>
              {!canAdmin && <div className="feature-panel" role="status"><AlertTriangle /> Administrator authority is required to change organization modules.</div>}
              <label className="wide">Change note
                <input value={moduleReason} disabled={!canAdmin} maxLength={500} placeholder="Optional audit note" onChange={(event) => setModuleReason(event.target.value)} />
              </label>
              <div className="settings-list module-settings-list" aria-label="Organization modules">
                {moduleViews.length === 0 && <p className="empty-inline">Loading module definitions...</p>}
                {moduleViews.map((view) => {
                  const dependencyNames = view.definition.dependencies.map((key) => getModuleDefinition(key).displayName);
                  const dependentNames = view.dependents.map((key) => getModuleDefinition(key).displayName);
                  const disabledDependencies = view.definition.dependencies.filter((key) => moduleEnabledMap[key] === false);
                  const enabledDependents = view.dependents.filter((key) => moduleEnabledMap[key] !== false);
                  const isLaunchDeferred = view.definition.availability === "launch_deferred";
                  const canDisable = view.definition.classification !== "core" && enabledDependents.length === 0;
                  const canEnable = !isLaunchDeferred && (disabledDependencies.length === 0 || canAdmin);
                  return (
                    <article key={view.definition.key} className="module-settings-row">
                      <div>
                        <strong>{view.definition.displayName}</strong>
                        <span>{view.definition.category} / {view.definition.classification === "core" ? "Core" : "Optional"} / {isLaunchDeferred ? "Launch deferred" : view.definition.maturity}</span>
                        <p>{view.definition.description}</p>
                        {isLaunchDeferred && <small>{view.definition.unavailableReason}</small>}
                        {dependencyNames.length > 0 && <small>Requires: {dependencyNames.join(", ")}</small>}
                        {dependentNames.length > 0 && <small>Required by: {dependentNames.join(", ")}</small>}
                        {view.definition.stopsBackgroundWork && <small>Background processing stops when disabled.</small>}
                        <small>{view.definition.disabledReadPolicy}</small>
                      </div>
                      <div className="module-state-actions">
                        <span className={view.state.enabled ? "status-pill success" : "status-pill muted"}>{view.state.enabled ? "Enabled" : "Disabled"}</span>
                        {view.definition.configurationRoute && view.state.enabled && <button onClick={() => onNavigate ? onNavigate(view.definition.configurationRoute ?? "/app/settings") : window.location.assign(view.definition.configurationRoute ?? "/app/settings")}>Configure</button>}
                        <button
                          className={view.state.enabled ? "" : "primary"}
                          disabled={!canAdmin || Boolean(modulePendingKey) || (view.state.enabled ? !canDisable : !canEnable)}
                          onClick={() => void toggleModule(view)}
                        >
                          {modulePendingKey === view.definition.key ? "Saving..." : view.state.enabled ? "Disable" : isLaunchDeferred ? "Unavailable" : "Enable"}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
              <p className="empty-inline">Module changes are tenant-isolated, audited, and preserve historical data. Server-side checks remain authoritative even when navigation hides disabled features.</p>
            </section>
                </div>
                <div className="modal-actions">
                  <button onClick={cancelActiveSettingsSection} disabled={Boolean(pending)}>Cancel</button>
                  <button className="primary" onClick={() => void run(activeSettingsSection ?? "settings", saveActiveSettingsSection)} disabled={!canSaveActiveSettingsSection}><Save /> {pending ? "Saving..." : "Save"}</button>
                </div>
              </section>
            </div>
          )}
        </>
      )}
      <ContextFooter items={[["Product", shellStoreProfile.productName ?? productBrand.name], ["Store", shellStoreProfile.storeDisplayName], ["Role", currentUser.role], ["Access", canAdmin ? "Administrator settings" : "Read-only settings"]]} />
    </section>
  );
}

type SettingsData = {
  locations: InventoryLocation[];
  shelves: InventoryShelf[];
  formats: CatalogFormat[];
  conditions: OperationalCondition[];
  templates: LabelTemplate[];
  defaults: InventoryDefaults;
};
type SettingsSectionCard = {
  id: SettingsSectionId;
  title: string;
  eyebrow: string;
  summary: string;
  icon: typeof Settings;
  disabled?: boolean;
};

function buildSettingsAreaCards(role: UserSession["role"]): SettingsAreaCard[] {
  const areas: SettingsAreaCard[] = [
    {
      title: "Loyalty",
      eyebrow: "Customers",
      summary: "Program rules, rewards, campaigns, balances, and ledger adjustments.",
      path: "/app/settings/loyalty",
      icon: HandCoins,
      roles: ["administrator", "manager"],
    },
    {
      title: "Storefront",
      eyebrow: "Public store",
      summary: "Publishing, homepage sections, collections, shipping, pickup, and custom domains.",
      path: "/app/settings/storefront",
      icon: BookOpen,
      roles: ["administrator", "manager"],
    },
    {
      title: "Onboarding",
      eyebrow: "Launch readiness",
      summary: "Subscription status, setup checklist, blockers, and launch review.",
      path: "/app/settings/onboarding",
      icon: Check,
      roles: ["administrator", "manager"],
    },
  ];
  return areas.filter((area) => area.roles.includes(role));
}

type LocationDraft = { id?: string; code: string; name: string; active: boolean };
type ShelfDraft = { id?: string; locationId: string; code: string; name?: string; description?: string; active: boolean };
type FormatDraft = { code: string; displayName: string; displayOrder: number; active: boolean };
type ConditionDraft = { code: string; displayName: string; description?: string; sortOrder: number; active: boolean };

const emptyLocationDraft: LocationDraft = { code: "", name: "", active: true };
const emptyShelfDraft: ShelfDraft = { locationId: "", code: "", name: "", description: "", active: true };
const emptyFormatDraft: FormatDraft = { code: "", displayName: "", displayOrder: 100, active: true };
const emptyConditionDraft: ConditionDraft = { code: "", displayName: "", description: "", sortOrder: 100, active: true };

function buildSettingsSections(input: {
  canAdmin: boolean;
  hasStoreProfile: boolean;
  activeLocationName: string;
  labelTemplateName: string;
  tradeInPolicy: TradeInPolicySettings;
  discounts: PosDiscountDefinition[];
  theme: ThemeMode;
  supplierDiagnostics: SupplierSearchDiagnostics | null;
  supplierProviders: SupplierSearchProvider[];
  data: SettingsData;
  moduleEnabledMap: Partial<Record<ModuleKey, boolean>>;
}): SettingsSectionCard[] {
  const sections: SettingsSectionCard[] = [
    { id: "modules", title: "Modules", eyebrow: "Features", summary: "Core and optional module enablement", icon: Settings, disabled: !input.canAdmin },
    { id: "appearance", title: "Appearance", eyebrow: "Appearance", summary: `${input.theme === "dark" ? "Dark" : "Light"} theme`, icon: SunMoon },
    { id: "operations", title: "Operations", eyebrow: "Operations", summary: `${input.activeLocationName} / ${input.data.defaults.defaultTrackingMode}`, icon: SlidersHorizontal },
    { id: "discounts", title: "Discounts", eyebrow: "Pricing", summary: `${input.discounts.filter((discount) => discount.active).length} active rules`, icon: Percent, disabled: !input.canAdmin },
    { id: "tradeIns", title: "Trade-ins", eyebrow: "Trade-ins", summary: input.tradeInPolicy.creditMode === "one_for_one" ? "1 book = 1 credit" : `${input.tradeInPolicy.defaultCreditPercentage}% dollar credit`, icon: HandCoins },
    { id: "profile", title: "Store Identity", eyebrow: "Store identity", summary: input.hasStoreProfile ? "Name, contact, public copy" : "Administrator only", icon: Building2, disabled: !input.canAdmin || !input.hasStoreProfile },
    { id: "suppliers", title: "Supplier Searches", eyebrow: "Sourcing", summary: `${input.supplierProviders.filter((provider) => provider.enabled).length} enabled / ${input.supplierDiagnostics?.serpApiConfigured ? "API ready" : "link mode"}`, icon: SearchCheck },
    { id: "structure", title: "Locations & Shelves", eyebrow: "Structure", summary: `${input.data.locations.length} locations / ${input.data.shelves.length} shelves`, icon: MapPinned },
    { id: "catalog", title: "Formats & Conditions", eyebrow: "Catalog setup", summary: `${input.data.formats.length} formats / ${input.data.conditions.length} conditions`, icon: Tags },
    { id: "labels", title: "Labels", eyebrow: "Labels", summary: input.labelTemplateName, icon: Printer },
  ];
  return sections.filter((section) => section.id !== "tradeIns" || input.moduleEnabledMap.trade_ins !== false);
}

function ExternalMetadataLinks({ links }: { links: NonNullable<BookMetadata["externalLinks"]> }) {
  const uniqueLinks = [...new Map(links.map((link) => [link.url, link])).values()].slice(0, 4);
  if (!uniqueLinks.length) return null;
  return (
    <div className="metadata-links">
      {uniqueLinks.map((link) => (
        <a key={link.url} href={link.url} target="_blank" rel="noreferrer">{link.label}</a>
      ))}
    </div>
  );
}

function normalizeSettingsData(response: InventorySettingsResponse, templates: LabelTemplate[], shellStoreProfile: PublicStoreProfile): SettingsData {
  const locations = response.locations ?? [];
  const shelves = response.shelves ?? [];
  const formats = response.formats ?? [];
  const conditions = response.conditions ?? [];
  const firstLocationId = locations[0]?.id ?? "";
  const defaults: InventoryDefaults = {
    defaultLocationId: response.defaults?.defaultLocationId ?? firstLocationId,
    defaultShelfId: response.defaults?.defaultShelfId,
    defaultFormatId: response.defaults?.defaultFormatId,
    defaultTrackingMode: response.defaults?.defaultTrackingMode ?? "quantity",
    defaultConditionId: response.defaults?.defaultConditionId,
    defaultCurrency: response.defaults?.defaultCurrency ?? shellStoreProfile.currencyCode ?? "USD",
    defaultLabelTemplateId: response.defaults?.defaultLabelTemplateId,
    autoGenerateBarcodes: response.defaults?.autoGenerateBarcodes ?? true,
    barcodePrefix: response.defaults?.barcodePrefix ?? "DB",
    barcodeMinWidth: response.defaults?.barcodeMinWidth ?? 6,
    receivingMetadataBehavior: response.defaults?.receivingMetadataBehavior ?? "suggest",
    duplicateScanBehavior: response.defaults?.duplicateScanBehavior ?? "increment_quantity",
    labelsPerCopy: response.defaults?.labelsPerCopy ?? 1,
    requirePriceOnReceive: response.defaults?.requirePriceOnReceive ?? true,
    requireCostOnReceive: response.defaults?.requireCostOnReceive ?? false,
    offerLabelBatchAfterReceiving: response.defaults?.offerLabelBatchAfterReceiving ?? true,
  };
  return { locations, shelves, formats, conditions, templates, defaults };
}

function normalizeTradeInPolicy(value: Partial<TradeInPolicySettings> & { basePercentage?: number } | undefined, fallbackCurrency = "USD"): TradeInPolicySettings {
  const merged = { ...defaultTradeInPolicySettings, ...(value ?? {}) };
  const categories = { ...defaultTradeInPolicySettings.redemptionRules.categories, ...(value?.redemptionRules?.categories ?? {}) };
  return {
    ...merged,
    creditMode: merged.creditMode === "one_for_one" ? "one_for_one" : "dollar_amount",
    defaultCreditPercentage: clampPercent(Number(value?.defaultCreditPercentage ?? value?.basePercentage ?? defaultTradeInPolicySettings.defaultCreditPercentage)),
    oneForOneCreditAmount: 1,
    minItemPriceAmount: nonnegativeInt(merged.minItemPriceAmount),
    maxItemPriceAmount: nonnegativeInt(Math.max(merged.maxItemPriceAmount, merged.minItemPriceAmount)),
    currency: /^[A-Z]{3}$/.test(merged.currency) ? merged.currency : fallbackCurrency,
    quoteExpirationMinutes: nonnegativeInt(merged.quoteExpirationMinutes),
    managerApprovalThresholdAmount: nonnegativeInt(merged.managerApprovalThresholdAmount),
    conditionAdjustments: { ...defaultTradeInPolicySettings.conditionAdjustments, ...(value?.conditionAdjustments ?? {}) },
    allowedReasons: Array.isArray(value?.allowedReasons) && value.allowedReasons.length > 0 ? value.allowedReasons : defaultTradeInPolicySettings.allowedReasons,
    redemptionRules: {
      default: clampTradeInRule(value?.redemptionRules?.default ?? defaultTradeInPolicySettings.redemptionRules.default),
      categories: Object.fromEntries(Object.entries(categories).map(([key, rule]) => [key, clampTradeInRule(rule)])),
    },
    policyText: merged.policyText ?? null,
  };
}

function normalizeLabelStyleSettings(value: Record<string, unknown> | undefined): LabelStyleSettings {
  return {
    labelBackgroundColor: normalizeHexColor(value?.labelBackgroundColor, defaultLabelStyleSettings.labelBackgroundColor),
    labelTextColor: normalizeHexColor(value?.labelTextColor, defaultLabelStyleSettings.labelTextColor),
  };
}

function normalizeHexColor(value: unknown, fallback: string): string {
  return typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value) ? value.toUpperCase() : fallback;
}

function tradeInPolicyPayload(policy: TradeInPolicySettings, modeChangeConversionAmount?: number): TradeInPolicySettings {
  const normalized = normalizeTradeInPolicy(policy, policy.currency);
  return modeChangeConversionAmount ? { ...normalized, modeChangeConversionAmount } : normalized;
}

function clampTradeInRule(rule: TradeInRedemptionRule): TradeInRedemptionRule {
  return { enabled: Boolean(rule.enabled), maxPercent: clampPercent(Number(rule.maxPercent)) };
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.floor(value)));
}

function nonnegativeInt(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

function minorUnitsToInput(amount: number): string {
  return (nonnegativeInt(amount) / 100).toFixed(2);
}

function inputToMinorUnits(value: string): number {
  const amount = Math.round(Number(value || 0) * 100);
  return Number.isFinite(amount) ? Math.max(0, amount) : 0;
}

function cleanDiscountDraft(draft: DiscountDraft): Omit<PosDiscountDefinition, "id" | "updatedAt"> & { id?: string } {
  const percentageValue = Math.round(Number(draft.value || 0) * 100);
  return {
    id: draft.id || undefined,
    name: draft.name.trim(),
    discountType: draft.discountType,
    valueAmount: draft.discountType === "percentage" ? nonnegativeInt(percentageValue) : inputToMinorUnits(draft.value),
    scope: draft.scope,
    applicationMode: draft.applicationMode,
    active: draft.active,
    requiresManager: false,
    requiresReason: false,
    locationId: undefined,
    startsAt: emptyToUndefined(draft.startsAt),
    endsAt: emptyToUndefined(draft.endsAt),
    daysOfWeek: draft.daysOfWeek,
    startTime: emptyToUndefined(draft.startTime),
    endTime: emptyToUndefined(draft.endTime),
    minimumSubtotalAmount: inputToMinorUnits(draft.minimumSubtotal),
    minimumItemCount: nonnegativeInt(Number(draft.minimumItemCount || 0)),
    minimumMatchingItemCount: nonnegativeInt(Number(draft.minimumMatchingItemCount || 0)),
    productType: draft.productType || undefined,
    formatMatch: emptyToUndefined(draft.formatMatch),
    conditionMatch: emptyToUndefined(draft.conditionMatch),
    skuMatch: undefined,
    titleMatch: emptyToUndefined(draft.titleMatch),
    priority: nonnegativeInt(draft.priority),
    stackable: draft.stackable,
  };
}

function discountToDraft(discount: PosDiscountDefinition): DiscountDraft {
  return {
    id: discount.id,
    name: discount.name,
    discountType: discount.discountType,
    value: discount.discountType === "percentage" ? (discount.valueAmount / 100).toString() : minorUnitsToInput(discount.valueAmount),
    scope: discount.scope,
    applicationMode: discount.applicationMode,
    active: discount.active,
    minimumSubtotal: discount.minimumSubtotalAmount ? minorUnitsToInput(discount.minimumSubtotalAmount) : "",
    minimumItemCount: discount.minimumItemCount ? String(discount.minimumItemCount) : "",
    minimumMatchingItemCount: discount.minimumMatchingItemCount ? String(discount.minimumMatchingItemCount) : "",
    productType: discount.productType ?? "",
    formatMatch: discount.formatMatch ?? "",
    conditionMatch: discount.conditionMatch ?? "",
    titleMatch: discount.titleMatch ?? "",
    startsAt: discount.startsAt?.slice(0, 10) ?? "",
    endsAt: discount.endsAt?.slice(0, 10) ?? "",
    startTime: discount.startTime ?? "",
    endTime: discount.endTime ?? "",
    daysOfWeek: discount.daysOfWeek,
    priority: discount.priority,
    stackable: discount.stackable,
  };
}

function formatDiscountValue(discount: PosDiscountDefinition, currency: string): string {
  return discount.discountType === "percentage" ? `${(discount.valueAmount / 100).toFixed(2).replace(/\.00$/, "")}% off` : `${formatCents(discount.valueAmount, currency)} off`;
}

function discountRuleSummary(discount: PosDiscountDefinition, currency: string): string {
  const pieces = [];
  if (discount.minimumSubtotalAmount > 0) pieces.push(`sale at least ${formatCents(discount.minimumSubtotalAmount, currency)}`);
  if (discount.minimumItemCount > 0) pieces.push(`${discount.minimumItemCount}+ items`);
  if (discount.minimumMatchingItemCount > 0) pieces.push(`${discount.minimumMatchingItemCount}+ matching items`);
  if (discount.productType) pieces.push(discount.productType);
  if (discount.formatMatch) pieces.push(`format: ${discount.formatMatch}`);
  if (discount.conditionMatch) pieces.push(`condition: ${discount.conditionMatch}`);
  if (discount.titleMatch) pieces.push(`title: ${discount.titleMatch}`);
  if (discount.daysOfWeek.length) pieces.push(discount.daysOfWeek.map((day) => day.slice(0, 3)).join(", "));
  if (discount.startTime || discount.endTime) pieces.push(`${discount.startTime || "open"}-${discount.endTime || "close"}`);
  return pieces.length ? pieces.join(" / ") : "Applies to every draft sale.";
}

function toggleString(values: string[], value: string, checked: boolean): string[] {
  return checked ? [...new Set([...values, value])] : values.filter((entry) => entry !== value);
}

function cleanLocationDraft(draft: LocationDraft): LocationDraft {
  return { id: draft.id || undefined, code: normalizeCode(draft.code), name: draft.name.trim(), active: draft.active };
}

function cleanShelfDraft(draft: ShelfDraft): ShelfDraft {
  return { id: draft.id || undefined, locationId: draft.locationId, code: normalizeCode(draft.code), name: emptyToUndefined(draft.name), description: emptyToUndefined(draft.description), active: draft.active };
}

function cleanFormatDraft(draft: FormatDraft): FormatDraft {
  return { code: normalizeCode(draft.code), displayName: draft.displayName.trim(), displayOrder: draft.displayOrder, active: draft.active };
}

function cleanConditionDraft(draft: ConditionDraft): ConditionDraft {
  return { code: normalizeCode(draft.code), displayName: draft.displayName.trim(), description: emptyToUndefined(draft.description), sortOrder: draft.sortOrder, active: draft.active };
}

function normalizeSupplierProviders(value?: unknown): SupplierSearchProvider[] {
  const stored = Array.isArray(value) ? value.filter(isSupplierSearchProvider) : [];
  const missingDefaults = defaultSupplierSearchProviders.filter((provider) => !stored.some((entry) => entry.id === provider.id));
  return [
    ...missingDefaults.map((provider) => ({ ...provider, capabilities: [...provider.capabilities], credentialRequirements: [...provider.credentialRequirements] })),
    ...stored.map((provider) => provider.id === "google_shopping" && provider.mode === "link" ? { ...provider, mode: "serpapi_google_shopping" as const } : provider),
  ];
}

function isSupplierSearchProvider(value: unknown): value is SupplierSearchProvider {
  if (!value || typeof value !== "object") return false;
  const provider = value as Partial<SupplierSearchProvider>;
  return typeof provider.id === "string"
    && typeof provider.name === "string"
    && typeof provider.enabled === "boolean"
    && ["link", "ebay_api", "google_books", "serpapi_google_shopping", "wholesale_fixture"].includes(String(provider.mode))
    && typeof provider.urlTemplate === "string";
}

function moveSupplierProvider(providers: SupplierSearchProvider[], from: number, to: number): SupplierSearchProvider[] {
  const next = [...providers];
  const [provider] = next.splice(from, 1);
  next.splice(to, 0, provider);
  return next;
}

function storeProfilePayload(profile: PublicStoreProfile): Omit<PublicStoreProfile, "productName"> {
  return {
    storeDisplayName: profile.storeDisplayName,
    storeTagline: profile.storeTagline,
    storeDescription: profile.storeDescription,
    logoUrl: profile.logoUrl,
    publicWebsiteUrl: profile.publicWebsiteUrl,
    publicEmail: profile.publicEmail,
    publicPhone: profile.publicPhone,
    addressLine1: profile.addressLine1,
    addressLine2: profile.addressLine2,
    city: profile.city,
    region: profile.region,
    postalCode: profile.postalCode,
    country: profile.country,
    socialLinks: profile.socialLinks,
    businessHours: profile.businessHours,
    timezone: profile.timezone,
    currencyCode: profile.currencyCode,
    receiptFooterText: profile.receiptFooterText,
    storefrontTitle: profile.storefrontTitle,
    storefrontDescription: profile.storefrontDescription,
  };
}

function readFileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the logo file."));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const [, payload] = result.split(",");
      if (payload) resolve(payload);
      else reject(new Error("Could not read the logo file."));
    };
    reader.readAsDataURL(file);
  });
}

function formatMm(value: number): string {
  return value.toFixed(value % 1 === 0 ? 0 : 1);
}

function mmToCssPx(value: number): number {
  return value * 96 / 25.4;
}

function normalizeCode(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");
}

function emptyToNull(value: string): string | null {
  return value.trim() || null;
}

function emptyToUndefined(value?: string): string | undefined {
  const text = value?.trim();
  return text || undefined;
}

function settingsErrorMessage(error: unknown): string {
  if (error instanceof Error && !(error instanceof StaffApiError)) return error.message;
  return staffErrorMessage(error);
}

async function readBatch(api: ApiClient, batchId: string): Promise<ReceivingBatch> {
  const data = await api<{ batch: ReceivingBatch }>(`/api/admin/receiving/batches/${encodeURIComponent(batchId)}`);
  return data.batch;
}

async function openPrintHtml(apiText: ApiClient, batchId: string, startingPosition: number): Promise<void> {
  const encodedBatchId = encodeURIComponent(batchId);
  await apiText<string>(`/api/admin/labels/batches/${encodedBatchId}/print`, {
    method: "POST",
    body: JSON.stringify({ idempotencyKey: retainedKey("label-print", batchId, JSON.stringify({ batchId, startingPosition })), startingPosition }),
  });
  const printUrl = `/api/admin/labels/batches/${encodedBatchId}/print?${new URLSearchParams({ startingPosition: String(startingPosition), t: String(Date.now()) })}`;
  const win = window.open(printUrl, "_blank");
  if (!win) throw new StaffApiError({ status: 409, code: "print_window_blocked", message: "Allow pop-ups to open browser print preview" });
  win.addEventListener("load", () => {
    win.focus();
    win.print();
  }, { once: true });
}

function filterLabel(filter: "recent" | "week" | "month" | "all") {
  if (filter === "week") return "Added this week";
  if (filter === "month") return "Added this month";
  if (filter === "all") return "All labelable inventory";
  return "Added today";
}

function labelInventoryKey(item: LabelInventoryItem): string {
  return item.copyId ?? `${item.variationId}:${item.locationId ?? "all"}`;
}

function labelInventoryCount(item: LabelInventoryItem): number {
  return Math.max(1, Number(item.labelCount ?? item.availableQuantity ?? 1));
}

function labelInventorySelection(item: LabelInventoryItem) {
  if (item.copyId) return { copyId: item.copyId };
  return { variationId: item.variationId, locationId: item.locationId };
}

function labelInventoryPreviewEntry(item: LabelInventoryItem) {
  return {
    copyId: item.copyId,
    variationId: item.variationId,
    storeBarcode: item.storeBarcode,
    inventoryNumber: item.inventoryNumber,
    title: item.title,
    contributorDisplay: item.contributorDisplay,
    format: item.format,
    condition: item.condition,
    sellingPriceAmount: item.sellingPriceAmount,
    currency: item.currency,
    location: item.location,
    shelf: item.shelf,
    labelCount: labelInventoryCount(item),
  };
}

function retainedKey(scope: string, identity: string, fingerprint: string): string {
  const storageKey = `spine:${scope}:${identity}`;
  const existing = localStorage.getItem(storageKey);
  if (existing) {
    try {
      const parsed = JSON.parse(existing) as { fingerprint: string; key: string };
      if (parsed.fingerprint === fingerprint && parsed.key) return parsed.key;
    } catch {
      localStorage.removeItem(storageKey);
    }
  }
  const key = crypto.randomUUID();
  localStorage.setItem(storageKey, JSON.stringify({ fingerprint, key }));
  return key;
}

function clearRetainedScope(scope: string): void {
  Object.keys(localStorage).filter((key) => key.startsWith(`spine:${scope}:`)).forEach((key) => localStorage.removeItem(key));
}

function metadataToReceivingLine(current: DraftLine, match: BookMetadata): DraftLine {
  const formatCode = inferFormat(match.format, current.formatCode);
  return {
    ...current,
    workTitle: match.title,
    contributorDisplay: match.authors.join(", "),
    isbn: match.isbn13 ?? match.isbn10 ?? current.isbn,
    productType: match.productType ?? current.productType,
    formatCode,
    metadata: { ...match, format: formatCode },
  };
}

function receivingLineToDraftLine(line: ReceivingLine, patch: Partial<DraftLine> = {}): DraftLine {
  const metadataBarcodes = Array.isArray((line.metadata as { storeBarcodes?: unknown[] } | undefined)?.storeBarcodes)
    ? ((line.metadata as { storeBarcodes?: string[] }).storeBarcodes ?? []).join("\n")
    : "";
  return {
    lineId: line.id,
    sourceIdentifier: line.sourceIdentifier,
    editionId: line.editionId,
    variationId: line.variationId,
    workTitle: line.workTitle,
    contributorDisplay: line.contributorDisplay ?? "",
    isbn: line.isbn ?? "",
    productType: line.productType ?? line.metadata?.productType ?? "book",
    formatCode: line.formatCode ?? "other",
    trackingMode: line.trackingMode,
    quantity: line.quantity,
    unitCostAmount: line.unitCostAmount,
    unitSellingPriceAmount: line.unitSellingPriceAmount,
    conditionCode: line.conditionCode,
    conditionNotes: line.conditionNotes ?? "",
    shelfCode: line.shelfCode ?? "",
    storeBarcodesText: metadataBarcodes,
    metadata: line.metadata,
    ...patch,
  };
}

function bestMetadataForReceiving(isbn: string, results: BookMetadata[]): BookMetadata | undefined {
  const normalized = normalizeIsbn(isbn);
  return results.find((metadata) => metadata.confidence === "exact_isbn" && [metadata.isbn13, metadata.isbn10].some((value) => normalizeIsbn(value ?? "") === normalized)) ?? results[0];
}

async function scanImageFileForBarcodes(file: File): Promise<string[]> {
  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await loadBarcodeImage(imageUrl);
    const nativeValues = await detectImageBarcodes(image);
    if (nativeValues.length) return uniqueBarcodes(nativeValues);
    const fallback = await decodeSingleImageBarcode(image);
    return fallback ? uniqueBarcodes([fallback]) : [];
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function loadBarcodeImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not read that photo."));
    image.src = url;
  });
}

async function detectImageBarcodes(image: HTMLImageElement): Promise<string[]> {
  const Detector = (globalThis as typeof globalThis & { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
  if (!Detector) return [];
  const detector = new Detector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"] });
  const detections = await detector.detect(image);
  return detections.map((barcode) => barcode.rawValue).filter(Boolean);
}

async function decodeSingleImageBarcode(image: HTMLImageElement): Promise<string | undefined> {
  try {
    const { BrowserMultiFormatReader } = await import("@zxing/browser");
    const reader = new BrowserMultiFormatReader();
    const result = await reader.decodeFromImageElement(image);
    return result.getText();
  } catch {
    return undefined;
  }
}

function summarizeBatch(batch: ReceivingBatch | null): string {
  if (!batch) return "No active batch";
  const lines = batch.lines ?? [];
  const quantity = lines.filter((entry) => entry.trackingMode === "quantity").reduce((sum, entry) => sum + entry.quantity, 0);
  const copies = lines.filter((entry) => entry.trackingMode === "copy").reduce((sum, entry) => sum + entry.quantity, 0);
  return `${lines.length} line(s), ${quantity} quantity unit(s), ${copies} exact copy/copies`;
}

function upsertReceivingLine(batch: ReceivingBatch, line: ReceivingLine): ReceivingBatch {
  return {
    ...batch,
    lines: upsertLineList(batch.lines ?? [], normalizeReceivingLineForDisplay(line)),
  };
}

function mergeReceivingLines(batch: ReceivingBatch, confirmedLines: ReceivingLine[] | undefined): ReceivingBatch {
  if (!confirmedLines?.length) return batch;
  return {
    ...batch,
    lines: confirmedLines.reduce((lines, line) => upsertLineList(lines, line), batch.lines ?? []),
  };
}

function batchContainsLine(batch: ReceivingBatch, line: ReceivingLine): boolean {
  return (batch.lines ?? []).some((entry) =>
    entry.id === line.id
    || (line.sourceIdentifier && entry.sourceIdentifier === line.sourceIdentifier)
    || (line.isbn && entry.isbn === line.isbn && entry.workTitle === line.workTitle)
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function upsertLineList(lines: ReceivingLine[], line: ReceivingLine): ReceivingLine[] {
  const normalizedLine = normalizeReceivingLineForDisplay(line);
  const existingIndex = lines.findIndex((entry) => entry.id === line.id);
  return existingIndex >= 0
    ? lines.map((entry) => entry.id === line.id ? normalizedLine : entry)
    : [...lines, normalizedLine].sort((a, b) => a.lineNumber - b.lineNumber);
}

function normalizeReceivingLineForDisplay(line: ReceivingLine): ReceivingLine {
  return {
    ...line,
    contributorDisplay: line.contributorDisplay ?? "",
    isbn: line.isbn ?? "",
    formatCode: line.formatCode ?? "other",
    conditionNotes: line.conditionNotes ?? "",
    shelfCode: line.shelfCode ?? "",
    metadata: line.metadata,
    validationErrors: Array.isArray(line.validationErrors) ? line.validationErrors : [],
    validationWarnings: Array.isArray(line.validationWarnings) ? line.validationWarnings : [],
    lineStatus: line.lineStatus ?? "ready",
  };
}

function inferFormat(providerFormat: string | undefined, fallback: string): string {
  const normalized = providerFormat?.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  if (!normalized || normalized === "book" || normalized === "print") return normalizeClientFormatCode(fallback);
  return normalizeClientFormatCode(normalized);
}

function normalizeClientFormatCode(value: string): string {
  const normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  if (["paperback", "softcover", "soft_cover", "trade"].includes(normalized)) return "trade_paperback";
  if (["massmarketpaperback", "mass_market", "mass_market_paperback", "mmpb"].includes(normalized)) return "mass_market_paperback";
  if (["blu_ray", "bluray", "blu"].includes(normalized)) return "blu_ray";
  if (["hardback", "hard_cover"].includes(normalized)) return "hardcover";
  if (["compact_disc"].includes(normalized)) return "cd";
  return normalized || "other";
}

function inferImportPriceKey(value?: string): ImportPriceKey {
  const normalized = String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "_");
  if (normalized.includes("blu_ray") || normalized.includes("bluray")) return "blu_ray";
  if (normalized.includes("video_game") || normalized.includes("game")) return "video_game";
  if (normalized.includes("mass_market")) return "mass_market_paperback";
  if (normalized.includes("hardcover") || normalized.includes("hardback") || normalized.includes("hard_cover")) return "hardcover";
  if (normalized.includes("paperback") || normalized.includes("softcover") || normalized.includes("soft_cover") || normalized.includes("trade_paperback")) return "softcover";
  if (normalized.includes("dvd")) return "dvd";
  if (normalized.includes("vhs")) return "vhs";
  if (normalized.includes("vinyl") || normalized.includes("record")) return "vinyl";
  if (normalized.includes("cassette")) return "cassette";
  if (normalized.includes("cd") || normalized.includes("compact_disc")) return "cd";
  if (normalized.includes("merch")) return "merchandise";
  if (normalized.includes("new")) return "new";
  if (normalized.includes("other")) return "other";
  return "softcover";
}

function formatProductType(value?: ProductType): string {
  return productTypeOptions.find((option) => option.value === value)?.label ?? "Book";
}

function normalizeScannedIdentifier(value: string): string {
  return value.replace(/\D/g, "") || value.trim().toLowerCase();
}

function uniqueBarcodes(values: string[]): string[] {
  return [...new Set(values.map(normalizeScannedIdentifier).filter(Boolean))];
}

function conditionCodeFromDefault(conditions: OperationalCondition[], defaultConditionId?: string): string | undefined {
  if (!defaultConditionId) return undefined;
  return conditions.find((condition) => condition.id === defaultConditionId || condition.code === defaultConditionId)?.code;
}

function defaultConditionIdFromCode(conditions: OperationalCondition[], conditionCode: string, fallback?: string): string | undefined {
  return conditions.find((condition) => condition.code === conditionCode || condition.id === conditionCode)?.id ?? fallback ?? conditions.find((condition) => condition.active)?.id;
}

function mergeReceivingAppSettings(settings: Partial<ReceivingAppSettings> | Record<string, unknown> | undefined, fallbackCurrency = "USD"): ReceivingAppSettings {
  const rawPrices = settings?.defaultImportPrices && typeof settings.defaultImportPrices === "object" ? settings.defaultImportPrices as Partial<Record<ImportPriceKey, Money>> : {};
  const currency = typeof settings?.defaultCurrency === "string" ? settings.defaultCurrency : fallbackCurrency ?? defaultReceivingAppSettings.defaultCurrency;
  const fallbackPrices = defaultReceivingAppSettings.defaultImportPrices;
  return {
    defaultCurrency: currency,
    defaultImportPrices: Object.fromEntries(receivingPriceDefaultOptions.map((option) => {
      const fallback = option.key === "mass_market_paperback" ? fallbackPrices.softcover : fallbackPrices[option.key];
      return [option.key, normalizeReceivingMoney(rawPrices[option.key], fallback, currency)];
    })) as Record<ImportPriceKey, Money>,
  };
}

function normalizeReceivingMoney(value: Money | undefined, fallback: Money, currency: string): Money {
  return { amount: Number.isFinite(value?.amount) ? Number(value?.amount) : fallback.amount, currency: value?.currency ?? currency };
}

function formatCents(amount: number, currency: string) {
  const safeCurrency = /^[A-Z]{3}$/.test(currency) ? currency : "USD";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: safeCurrency }).format(amount / 100);
}
